import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getPurchaseOrders, savePurchaseOrder, getNextInvoiceNo, getPurchaseOrderForReturn, getPurchaseReturnById, exportPurchaseSummaryToExcel, exportPurchaseDetailsToExcel, exportPurchaseReturnSummaryToExcel, exportPurchaseReturnDetailsToExcel } from "../../components/Api/GmsPurchaseOrder";
import { getPurchaseRequests, markPurchaseRequestAsReceived } from "../../components/Api/GmsPurchaseRequest";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSuppliers, searchSuppliers } from "../../components/Api/GmsSupplier";
import { getProducts } from "../../components/Api/GmsProduct";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { getTaxes } from "../../components/Api/GmsTax";
import { getProductPreviousPrices } from "../../components/Api/GmsProductPrices";
import { Eye, SquarePen, Plus, Minus, X, RotateCcw, Download } from "lucide-react";
import StockSelectionModal from "../../components/StockSelectionModal";
import StockDetailsModal from "../../components/StockDetailsModal";
import { checkStockSelectionLocks, deleteStockSelectionLock } from "../../components/Api/GmsStockSelectionLock";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  PurchaseOrder_Id: null,
  PurchaseOrder_InvoiceNo: "",
  PurchaseOrder_RefNo: "",
  PurchaseOrder_Supplier_Id: null,
  PurchaseOrder_InvoiceType_Id: 1, // 1 = Cash, 2 = Credit
  PurchaseOrder_Date: new Date(),
  PurchaseOrder_DueDate: new Date(),
  PurchaseOrder_Amount: 0,
  PurchaseOrder_DiscountPercent: 0,
  PurchaseOrder_DiscountAmount: 0,
  PurchaseOrder_OtherAmount: 0,
  PurchaseOrder_TaxAmount: 0,
  PurchaseOrder_OtherCharges: 0,
  PurchaseOrder_AdditionalDiscount: 0,
  PurchaseOrder_NetAmount: 0,
  PurchaseOrder_Notes: "",
  PurchaseOrder_TotalQty: 0,
  PurchaseOrder_Branch_Id: null,
  PurchaseOrder_TotalAmount: 0,
  PurchaseOrder_CreatedDate: new Date().toISOString(),
  PurchaseOrder_CreatedBy: 1,
  PurchaseOrder_Business_Id: null,
  Details: [],
};

const emptyFilter = {
  PurchaseOrder_Id: null,
  PurchaseOrder_InvoiceNo: null,
  PurchaseOrder_Supplier_Id: null,
  PurchaseOrder_Business_Id: null,
  From_Date: null,
  To_Date: null,
};

const defaultDetail = {
  PurchaseOrderDet_Id: null,
  PurchaseOrderDet_Product_Id: null,
  PurchaseOrderDet_Unit_Id: 1,
  PurchaseOrderDet_Quantity: 0,
  PurchaseOrderDet_BonusQuantity: 0,
  PurchaseOrderDet_UnitPrice: 0,
  PurchaseOrderDet_DiscountPercent: 0,
  PurchaseOrderDet_DiscountAmount: 0,
  PurchaseOrderDet_TotalAmount: 0, // Added TotalAmount field
  PurchaseOrderDet_NetAmount: 0,
  PurchaseOrderDet_TaxPercent: 0,
  Product_NameEn: "",
  Unit_NameEn: "",
  TaxDetails: [],
};

const defaultStock = {
  batchQuantity: 0, // Quantity for this batch
  PurchaseStock_BatchNo: "",
  PurchaseStock_ExpDate: null,
  PurchaseStock_SalesPrice: 0,
  PurchaseStock_CostPrice: 0,
  PurchaseStock_IsBonus: false,
};

// Date validation function for DD/MM/YYYY format
const isValidDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') {
    return false;
  }
  
  // Check format DD/MM/YYYY
  const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateStr.match(datePattern);
  
  if (!match) {
    return false;
  }
  
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  
  // Check if month is valid (1-12)
  if (month < 1 || month > 12) {
    return false;
  }
  
  // Check if day is valid for the month
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) {
    return false;
  }
  
  // Check if year is reasonable (e.g., between 1900 and 2100)
  if (year < 1900 || year > 2100) {
    return false;
  }
  
  // Create a date object to verify it's a valid date
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }
  
  return true;
};

export default function PurchasePage() {
  const navigate = useNavigate();
  
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPurchase');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsPurchase');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsPurchase');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsPurchase');
  const isAdmin = isUserAdmin();

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [details, setDetails] = useState([defaultDetail]);
  
  // Debug logging removed to reduce console noise
  
  

  // Helper function to get field value with fallback
  const getDetailField = (detail, fieldName) => {
    // Handle special cases for product and unit names
    if (fieldName === 'Product_NameEn') {
      return detail.Product_NameEn || detail.product_NameEn || '';
    }
    if (fieldName === 'Unit_NameEn') {
      return detail.Unit_NameEn || detail.unit_NameEn || '';
    }
    // For other fields, try multiple variations
    if (detail[fieldName] !== undefined) {
      return detail[fieldName];
    }
    const lowercaseFieldName = fieldName.replace('PurchaseOrderDet_', 'purchaseOrderDet_');
    if (detail[lowercaseFieldName] !== undefined) {
      return detail[lowercaseFieldName];
    }
    const lowercaseFieldName2 = fieldName.replace('PurchaseOrder_', 'purchaseOrder_');
    if (detail[lowercaseFieldName2] !== undefined) {
      return detail[lowercaseFieldName2];
    }
    const lowercaseFieldName3 = fieldName.replace('PurchaseStock_', 'purchaseStock_');
    if (detail[lowercaseFieldName3] !== undefined) {
      return detail[lowercaseFieldName3];
    }
    const lowercaseFieldName4 = fieldName.replace('PurchaseOrderTaxDet_', 'purchaseOrderTaxDet_');
    if (detail[lowercaseFieldName4] !== undefined) {
      return detail[lowercaseFieldName4];
    }
    return '';
  };

  // Helper function to format numeric value for display (empty string when 0)
  const formatNumericValue = (value) => {
    const numValue = parseFloat(value) || 0;
    return numValue === 0 ? '' : numValue;
  };
  const [isEditing, setIsEditing] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isReturnMode, setIsReturnMode] = useState(false);
  const [returnPurchaseOrderId, setReturnPurchaseOrderId] = useState(null);
  const [originalInvoiceNo, setOriginalInvoiceNo] = useState(null);
  const [originalPurchaseOrderForReturn, setOriginalPurchaseOrderForReturn] = useState(null);
  // Stock selection lock - unique form ID (only for return mode)
  const [temporaryFormUniqueId, setTemporaryFormUniqueId] = useState(null);
  // Ref to track the last business ID we generated invoice for (to prevent duplicate calls)
  const lastGeneratedBusinessIdRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("2");
  const [showEditForm, setShowEditForm] = useState(false);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [formKey, setFormKey] = useState(0);
  
  // Scroll helpers: always scroll to form on Edit/View/Return
  const formTopRef = useRef(null);

  const scrollToFormTop = () => {
    // Defer until after render
    setTimeout(() => {
      if (formTopRef.current && typeof formTopRef.current.scrollIntoView === 'function') {
        formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 0);
  };
  
  // Supplier search states
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchTimeout, setSupplierSearchTimeout] = useState(null);
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);
  const [selectedSupplierFilterOption, setSelectedSupplierFilterOption] = useState(null);
  
  // Modal states
  const [showStockModal, setShowStockModal] = useState(false);
  const [showStockSelectionModal, setShowStockSelectionModal] = useState(false);
  const [currentDetailIndex, setCurrentDetailIndex] = useState(0);
  const [stockDetails, setStockDetails] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [previousPrices, setPreviousPrices] = useState(null);
  const [selectedStockForReturn, setSelectedStockForReturn] = useState({});
  const [preloadedStockItems, setPreloadedStockItems] = useState(null);
  // Stock details modal (view-only)
  const [showStockDetailsModal, setShowStockDetailsModal] = useState(false);
  const [currentStockDetails, setCurrentStockDetails] = useState([]);
  const [currentStockDetailsProduct, setCurrentStockDetailsProduct] = useState("");

  // Purchase Order selection modal states
  const [showPurchaseOrderModal, setShowPurchaseOrderModal] = useState(false);
  const [purchaseOrdersList, setPurchaseOrdersList] = useState([]);
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState(null);
  const [selectedPurchaseOrderData, setSelectedPurchaseOrderData] = useState(null);
  const [markAsReceived, setMarkAsReceived] = useState(false);
  const [originalPurchaseOrderQuantities, setOriginalPurchaseOrderQuantities] = useState({});
  
  // Export loading states
  const [exportingSummary, setExportingSummary] = useState(false);
  const [exportingReturnSummary, setExportingReturnSummary] = useState(false);
  const [exportingPurchaseId, setExportingPurchaseId] = useState(null); // Track which purchase is being exported
  const [exportingReturnId, setExportingReturnId] = useState(null); // Track which return is being exported

  // Rounding helper: standard half-up to 2 decimals
  const roundHalfUp = (value, decimals = 2) => {
    const factor = Math.pow(10, decimals);
    return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
  };

  // Format date as dd/MM/yyyy
  const formatDateDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  // Generate unique form ID for stock selection lock (Purchase Return only)
  const generateTemporaryFormUniqueId = () => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, ''); // HHMMSS
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const formId = `PR-${dateStr}-${timeStr}-${sequence}`;
    
    // Store form ID in sessionStorage so we can clean it up on refresh
    const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
    storedFormIds.push(formId);
    sessionStorage.setItem('stockLockFormIds', JSON.stringify(storedFormIds));
    
    return formId;
  };

  useEffect(() => {
    if (hasPermission) {
      // Cleanup stock locks on page load/refresh
      // Since the form is cleared, we should also clear any locks from previous session
      const cleanupLocksOnRefresh = async () => {
        try {
          const { cleanupStockSelectionLocks, deleteStockSelectionLock } = await import('../components/Api/GmsStockSelectionLock');
          
          // 1. Clean up any form IDs stored in sessionStorage (from previous session before refresh)
          const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
          console.log('🔒 Cleaning up locks for stored form IDs:', storedFormIds);
          for (const formId of storedFormIds) {
            try {
              await deleteStockSelectionLock(formId);
              console.log('✅ Cleaned up locks for form:', formId);
            } catch (err) {
              console.warn('Failed to cleanup locks for form:', formId, err);
            }
          }
          // Clear the stored form IDs
          sessionStorage.removeItem('stockLockFormIds');
          
          // 2. Also clean up locks older than 5 minutes (very short time to catch any missed ones)
          // This ensures any locks from the previous session are cleared
          await cleanupStockSelectionLocks(0.083).catch(err => { // 0.083 hours = 5 minutes
            console.warn('Failed to cleanup old locks on page load:', err);
          });
          console.log('✅ Cleaned up locks older than 5 minutes');
        } catch (error) {
          console.warn('Error during lock cleanup on page load:', error);
        }
      };
      cleanupLocksOnRefresh();
      
      loadBusinesses();
      loadBranches();
      loadSuppliers();
      loadProducts();
      loadUnits();
      loadTaxes();
      loadPurchaseOrders();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      } else {
        setActiveTab("2"); // Default to list tab
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadPurchaseOrders();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  // Cleanup stock locks on page refresh/navigation (only for return mode)
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (isReturnMode && temporaryFormUniqueId) {
        try {
          await deleteStockSelectionLock(temporaryFormUniqueId);
        } catch (error) {
          console.error('Error cleaning up locks on page unload:', error);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also cleanup on component unmount
      if (isReturnMode && temporaryFormUniqueId) {
        deleteStockSelectionLock(temporaryFormUniqueId).catch(err => 
          console.error('Error cleaning up locks on unmount:', err)
        );
      }
    };
  }, [isReturnMode, temporaryFormUniqueId]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({});
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load companies", "error");
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({});
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  // Auto-populate company if user has companyId
  useEffect(() => {
    const userCompanyId = localStorage.getItem("userCompanyId");
    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists && !form.PurchaseOrder_Business_Id) {
        setForm(prev => ({
          ...prev,
          PurchaseOrder_Business_Id: parseInt(userCompanyId)
        }));
      }
    }
  }, [businesses]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    const userBranchId = localStorage.getItem("userBranchId");
    if (userBranchId && branches.length > 0 && form.PurchaseOrder_Business_Id) {
      const branchExists = branches.find(b => b.branch_Id === parseInt(userBranchId) && b.branch_Buisness_Id === form.PurchaseOrder_Business_Id);
      if (branchExists && !form.PurchaseOrder_Branch_Id) {
        setForm(prev => ({
          ...prev,
          PurchaseOrder_Branch_Id: parseInt(userBranchId)
        }));
      }
    }
  }, [branches, form.PurchaseOrder_Business_Id]);

  // Auto-generate invoice number when business is selected (manually or automatically)
  useEffect(() => {
    // Only generate invoice number if:
    // 1. Business is selected
    // 2. Not in view mode
    // 3. Not in return mode (return mode has its own logic)
    // 4. Invoice number is not already set
    // 5. We haven't already generated for this business ID (to prevent duplicate calls)
    if (
      form.PurchaseOrder_Business_Id && 
      !isViewMode && 
      !isReturnMode && 
      !form.PurchaseOrder_InvoiceNo &&
      lastGeneratedBusinessIdRef.current !== form.PurchaseOrder_Business_Id
    ) {
      const generateInvoiceNo = async () => {
        try {
          // Mark that we're generating for this business ID
          lastGeneratedBusinessIdRef.current = form.PurchaseOrder_Business_Id;
          
          const result = await getNextInvoiceNo(form.PurchaseOrder_Business_Id, false, null);
          if (result.nextInvoiceNo) {
            setForm(prev => ({
              ...prev,
              PurchaseOrder_InvoiceNo: result.nextInvoiceNo
            }));
          }
        } catch (error) {
          console.error("Error getting next invoice number:", error);
          // Reset ref on error so we can retry
          lastGeneratedBusinessIdRef.current = null;
        }
      };
      
      generateInvoiceNo();
    }
  }, [form.PurchaseOrder_Business_Id, isViewMode, isReturnMode, form.PurchaseOrder_InvoiceNo]);

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers({});
      setSuppliers(data || []);
    } catch (error) {
      console.error("Error loading suppliers:", error);
      Swal.fire("Error", "Failed to load suppliers", "error");
    }
  };

  // Manual search function for supplier search
  const searchSuppliersManually = async (inputValue) => {
    console.log('🔍 searchSuppliersManually called with inputValue:', inputValue);
    
    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierSearchOptions([]);
      return;
    }
    
    try {
      setSupplierSearchLoading(true);
      console.log('🚀 Making API call to searchSuppliers with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        
        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      
      console.log('📋 Formatted options:', formattedOptions);
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  // Manual search function for supplier filter search
  const searchSuppliersFilterManually = async (inputValue) => {
    console.log('🔍 searchSuppliersFilterManually called with inputValue:', inputValue);
    
    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierSearchOptions([]);
      return;
    }
    
    try {
      setSupplierSearchLoading(true);
      console.log('🚀 Making API call to searchSuppliers with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        
        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      
      console.log('📋 Formatted options:', formattedOptions);
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({});
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Failed to load products", "error");
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnits(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      Swal.fire("Error", "Failed to load units", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      setLoading(true);
      // For list view, exclude stock details to optimize performance
      const filterWithStockDetails = {
        ...filter,
        IncludeStockDetails: false
      };
      const data = await getPurchaseOrders(filterWithStockDetails);
      setPurchaseOrders(data || []);
      setTotalCount(data?.length || 0);
    } catch (error) {
      console.error("Error loading purchase:", error);
      Swal.fire("Error", "Failed to load purchase", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessChange = async (businessId) => {
    setForm(prev => ({
      ...prev,
      PurchaseOrder_Business_Id: businessId,
      PurchaseOrder_Branch_Id: null
    }));

    if (businessId) {
      try {
        const result = await getNextInvoiceNo(businessId, isReturnMode, isReturnMode ? originalInvoiceNo : null);
        console.log("Invoice number result:", result);
        if (result.nextInvoiceNo) {
          setForm(prev => ({
            ...prev,
            PurchaseOrder_InvoiceNo: result.nextInvoiceNo
          }));
        }
      } catch (error) {
        console.error("Error getting next invoice number:", error);
      }
    }
  };

  const handlePurchaseReturn = async (purchaseOrder) => {
    try {
      setLoading(true);
      // IMPORTANT: We fetch FRESH data from database on click to handle race conditions
      // This ensures we get the latest stock status even if another user returned stocks
      // after the purchase list was loaded. The stored procedure GMS_Select_PurchaseOrder_ForReturn
      // filters out already returned stocks (LastTransFlag NOT IN 3, 4, 7) at query time.
      const purchaseOrderData = await getPurchaseOrderForReturn(purchaseOrder.purchaseOrder_Id || purchaseOrder.PurchaseOrder_Id);
      
      if (!purchaseOrderData) {
        Swal.fire("Error", "Failed to load purchase order for return", "error");
        setLoading(false);
        return;
      }

      // Check if there are any returnable stocks
      // The stored procedure already filters out returned stocks (LastTransFlag NOT IN 3, 4, 7)
      // So we check if any detail has stockDetails with returnable stocks
      // This check uses FRESH data from the database, not cached list data
      let hasReturnableStocks = false;
      if (purchaseOrderData.details && purchaseOrderData.details.length > 0) {
        hasReturnableStocks = purchaseOrderData.details.some(detail => 
          detail.stockDetails && 
          Array.isArray(detail.stockDetails) && 
          detail.stockDetails.length > 0
        );
      }

      if (!hasReturnableStocks) {
        setLoading(false);
        Swal.fire({
          icon: "info",
          title: "No Returnable Stocks",
          text: "All stocks from this purchase have already been returned. There are no stocks available for return.",
          confirmButtonColor: "#556ee6"
        });
        return;
      }

      // Store original purchase order data for return mode
      setOriginalPurchaseOrderForReturn(purchaseOrderData);

      // Set form data
      setForm({
        PurchaseOrder_Id: null,
        PurchaseOrder_InvoiceNo: "",
        PurchaseOrder_RefNo: purchaseOrderData.purchaseOrder_RefNo || "",
        PurchaseOrder_Supplier_Id: purchaseOrderData.purchaseOrder_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: purchaseOrderData.purchaseOrder_InvoiceType_Id,
        PurchaseOrder_Date: new Date(),
        PurchaseOrder_DueDate: purchaseOrderData.purchaseOrder_DueDate ? new Date(purchaseOrderData.purchaseOrder_DueDate) : new Date(),
        PurchaseOrder_Amount: 0,
        PurchaseOrder_DiscountPercent: 0,
        PurchaseOrder_DiscountAmount: 0,
        PurchaseOrder_OtherAmount: 0,
        PurchaseOrder_TaxAmount: 0,
        PurchaseOrder_OtherCharges: 0,
        PurchaseOrder_AdditionalDiscount: 0,
        PurchaseOrder_NetAmount: 0,
        PurchaseOrder_Notes: purchaseOrderData.purchaseOrder_Notes || "",
        PurchaseOrder_TotalQty: 0,
        PurchaseOrder_Branch_Id: purchaseOrderData.purchaseOrder_Branch_Id,
        PurchaseOrder_TotalAmount: 0,
        PurchaseOrder_CreatedDate: new Date().toISOString(),
        PurchaseOrder_CreatedBy: getUserSession().userId,
        PurchaseOrder_Business_Id: purchaseOrderData.purchaseOrder_Business_Id,
        Supplier_NameEn: purchaseOrderData.supplier_NameEn,
        InvoiceType_Name: purchaseOrderData.invoiceType_Name
      });

      // Cleanup any existing locks before entering return mode
      if (temporaryFormUniqueId) {
        try {
          await deleteStockSelectionLock(temporaryFormUniqueId);
        } catch (error) {
          console.error('Error cleaning up locks before return mode:', error);
        }
      }
      
      // Set return mode
      setIsReturnMode(true);
      setReturnPurchaseOrderId(purchaseOrderData.purchaseOrder_Id);
      setOriginalInvoiceNo(purchaseOrderData.purchaseOrder_InvoiceNo);
      setIsEditing(false);
      setIsViewMode(false);
      setShowEditForm(true); // Show the form for return mode
      // Reset form ID (will be generated when stock modal opens)
      setTemporaryFormUniqueId(null);

      // Load details
      if (purchaseOrderData.details && purchaseOrderData.details.length > 0) {
        const mappedDetails = purchaseOrderData.details.map((detail) => ({
          PurchaseOrderDet_Id: null,
          PurchaseOrderDet_Product_Id: detail.purchaseOrderDet_Product_Id,
          PurchaseOrderDet_Unit_Id: detail.purchaseOrderDet_Unit_Id,
          PurchaseOrderDet_Quantity: 0,
          PurchaseOrderDet_BonusQuantity: 0,
          PurchaseOrderDet_UnitPrice: detail.purchaseOrderDet_UnitPrice,
          PurchaseOrderDet_DiscountPercent: detail.purchaseOrderDet_DiscountPercent || 0,
          PurchaseOrderDet_DiscountAmount: 0,
          PurchaseOrderDet_TotalAmount: 0,
          PurchaseOrderDet_NetAmount: 0,
          PurchaseOrderDet_TaxPercent: detail.purchaseOrderDet_TaxPercent || 0,
          PurchaseOrderDet_TaxAmount: 0,
          Product_NameEn: detail.product_NameEn,
          Unit_NameEn: detail.unit_NameEn,
          TaxDetails: detail.taxDetails || [],
          stockDetails: detail.stockDetails || [],
          originalQuantity: detail.purchaseOrderDet_Quantity,
          originalBonusQuantity: detail.purchaseOrderDet_BonusQuantity || 0,
          originalAvailableStockCount: (detail.stockDetails && Array.isArray(detail.stockDetails)) ? detail.stockDetails.length : 0
        }));
        // In return mode, hide products with no eligible stock
        const eligibleOnly = mappedDetails.filter(d => Array.isArray(d.stockDetails) && d.stockDetails.length > 0);
        setDetails(eligibleOnly.length > 0 ? eligibleOnly : [{ ...defaultDetail }]);
      } else {
        setDetails([{ ...defaultDetail }]);
      }

      // Generate return invoice number
      if (purchaseOrderData.purchaseOrder_Business_Id && purchaseOrderData.purchaseOrder_InvoiceNo) {
        try {
          const result = await getNextInvoiceNo(purchaseOrderData.purchaseOrder_Business_Id, true, purchaseOrderData.purchaseOrder_InvoiceNo);
          if (result.nextInvoiceNo) {
            setForm(prev => ({
              ...prev,
              PurchaseOrder_InvoiceNo: result.nextInvoiceNo
            }));
          }
        } catch (error) {
          console.error("Error getting return invoice number:", error);
        }
      }

      setActiveTab("2");
      scrollToFormTop();
      setLoading(false);
    } catch (error) {
      console.error("Error loading purchase order for return:", error);
      Swal.fire("Error", "Failed to load purchase order for return", "error");
      setLoading(false);
    }
  };

  const handleStockSelectedForReturn = (stockData) => {
    const detailIndex = currentDetailIndex;
    const detail = details[detailIndex];
    
    if (!detail) return;

    // Calculate quantities from selected stock
    let regularQty = 0;
    let bonusQty = 0;
    
    stockData.forEach(group => {
      group.stocks.forEach(stock => {
        if (stock.purchaseStock_IsBonus || stock.purchaseStockIsBonus) {
          bonusQty += 1;
        } else {
          regularQty += 1;
        }
      });
    });

    // Update detail with new quantities
    const newDetails = [...details];
    newDetails[detailIndex] = {
      ...newDetails[detailIndex],
      PurchaseOrderDet_Quantity: regularQty,
      PurchaseOrderDet_BonusQuantity: bonusQty,
      selectedStockForReturn: stockData
    };

    // Recalculate amounts
    calculateDetailAmounts(newDetails, detailIndex);
    recalculateTotalsFromDetails(newDetails);
    setDetails(newDetails);

    // Store selected stock for this detail
    setSelectedStockForReturn(prev => ({
      ...prev,
      [detailIndex]: stockData
    }));

    setShowStockSelectionModal(false);
  };

  // Open stock details modal for view-only mode in returns
  const openStockDetailsModal = (index) => {
    const detail = details[index];
    const productName = getDetailField(detail, 'Product_NameEn');
    const stocks = detail?.stockDetails || [];
    console.log('[ReturnView] Opening StockDetailsModal', { index, productName, stockCount: stocks.length });
    setCurrentStockDetailsProduct(productName || 'Product');
    setCurrentStockDetails(stocks);
    setShowStockDetailsModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDetailChange = async (index, field, value) => {
    // Validate quantity limits - TEMPORARILY COMMENTED OUT FOR TESTING
    // if (field === 'PurchaseOrderDet_Quantity') {
    //   if (value > 5000) {
    //     Swal.fire({
    //       icon: 'warning',
    //       title: 'Quantity Limit Exceeded',
    //       text: 'Maximum quantity allowed is 5000. Please enter a value between 0 and 5000.',
    //       confirmButtonText: 'OK'
    //     });
    //     value = 5000; // Cap at 5000
    //   }
    // } else if (field === 'PurchaseOrderDet_BonusQuantity') {
    //   if (value > 1000) {
    //     Swal.fire({
    //       icon: 'warning',
    //       title: 'Bonus Quantity Limit Exceeded',
    //       text: 'Maximum bonus quantity allowed is 1000. Please enter a value between 0 and 1000.',
    //       confirmButtonText: 'OK'
    //     });
    //     value = 1000; // Cap at 1000
    //   }
    // }
    
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    
    // Auto-fetch product details
    if (field === 'PurchaseOrderDet_Product_Id' && value) {
      const product = products.find(p => p.product_Id === value);
      if (product) {
        // Get unit information - extract only the name part
        const unit = units.find(u => u.value === product.unit_Id);
        const unitName = unit ? unit.label.split(' - ')[1] || unit.label : "Unit";
        
        // Calculate tax percentage from taxIds and populate TaxDetails
        let taxPercent = 0;
        let taxDetails = [];
        if (product.taxIds && product.taxIds.length > 0) {
          const productTaxes = taxes.filter(tax => product.taxIds.includes(tax.tax_Id));
          taxPercent = productTaxes.reduce((sum, tax) => sum + (tax.tax_Perc || 0), 0);
          taxDetails = productTaxes.map(tax => ({
            PurchaseOrderTaxDet_Tax_Id: tax.tax_Id,
            Tax_NameEn: tax.tax_NameEn,
            Tax_Perc: tax.tax_Perc
          }));
        }
        
        // Fetch previous unit price
        let previousUnitPrice = product.product_CostPrice || 0;
        try {
          const prices = await getProductPreviousPrices(value);
          console.log('=== UNIT PRICE DEBUG ===');
          console.log('Raw API response for unit price:', prices);
          console.log('PreviousUnitPrice from API:', prices?.previousUnitPrice);
          if (prices?.previousUnitPrice) {
            previousUnitPrice = prices.previousUnitPrice;
            console.log('Using previous unit price:', previousUnitPrice);
          } else {
            console.log('No previous unit price found, using product cost price:', previousUnitPrice);
          }
        } catch (error) {
          console.error('Error fetching previous unit price:', error);
        }
        
        newDetails[index] = {
          ...newDetails[index],
          PurchaseOrderDet_UnitPrice: previousUnitPrice,
          PurchaseOrderDet_TaxPercent: taxPercent,
          PurchaseOrderDet_Unit_Id: product.unit_Id || 1,
          Product_NameEn: product.product_NameEn,
          Unit_NameEn: unitName,
          expiryRequired: product.expiryRequired || false,
          TaxDetails: taxDetails
        };
      }
    }
    
    // Calculate amounts - trigger calculation for all relevant fields
    if (field === 'PurchaseOrderDet_Quantity' || field === 'PurchaseOrderDet_BonusQuantity' || 
        field === 'PurchaseOrderDet_UnitPrice' || field === 'PurchaseOrderDet_DiscountPercent' ||
        field === 'PurchaseOrderDet_TaxPercent') {
      calculateDetailAmounts(newDetails, index);
      
      // Recalculate totals when details change in edit mode
      if (isEditing) {
        recalculateTotalsFromDetails(newDetails);
      }
    }
    
    setDetails(newDetails);
  };

  const recalculateTotalsFromDetails = (detailsArray) => {
    // Calculate totals from details when user makes changes in edit mode
    let totalAmount = 0; // Amount before discount
    let totalDiscount = 0;
    let totalTax = 0;
    let totalQty = 0;
    
    detailsArray.forEach((detail, index) => {
      if (detail && (detail.PurchaseOrderDet_Product_Id || detail.purchaseOrderDet_Product_Id)) {
        // TotalAmount now represents amount before discount
        const totalAmountValue = parseFloat(detail.PurchaseOrderDet_TotalAmount || detail.purchaseOrderDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseOrderDet_DiscountAmount || detail.purchaseOrderDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseOrderDet_TaxAmount || detail.purchaseOrderDet_TaxAmount) || 0;
        const quantity = parseFloat(detail.PurchaseOrderDet_Quantity || detail.purchaseOrderDet_Quantity) || 0;
        const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity || detail.purchaseOrderDet_BonusQuantity) || 0;
        
        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
        totalQty += (quantity + bonusQuantity);  // Sum of qty + bonus qty
      }
    });
    
    // Round footer components
    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);
    totalQty = roundHalfUp(totalQty, 4);  // Round to 4 decimal places for quantity

    // For return mode: only show OtherCharges and AdditionalDiscount if stocks have been selected
    // Check if any detail has quantity > 0 (stocks selected)
    const hasSelectedStocks = detailsArray.some(detail => {
      const quantity = parseFloat(detail.PurchaseOrderDet_Quantity || detail.purchaseOrderDet_Quantity) || 0;
      return quantity > 0;
    });

    let otherCharges = 0;
    let additionalDiscount = 0;

    if (isReturnMode && hasSelectedStocks && originalPurchaseOrderForReturn) {
      // Use original values from the purchase order when stocks are selected
      otherCharges = parseFloat(originalPurchaseOrderForReturn.purchaseOrder_OtherCharges) || 0;
      additionalDiscount = parseFloat(originalPurchaseOrderForReturn.purchaseOrder_AdditionalDiscount) || 0;
    } else if (!isReturnMode) {
      // For regular purchase mode, use form values
      otherCharges = parseFloat(form.PurchaseOrder_OtherCharges) || 0;
      additionalDiscount = parseFloat(form.PurchaseOrder_AdditionalDiscount) || 0;
    }

    // Net Amount = (Total Amount - Total Discount) + Total Tax + Other Charges - Additional Discount
    const amountAfterDiscount = totalAmount - totalDiscount;
    const netAmount = roundHalfUp(amountAfterDiscount + totalTax + otherCharges - additionalDiscount, 2);
    
    setForm(prev => ({
      ...prev,
      PurchaseOrder_TotalAmount: totalAmount, // Amount before discount
      PurchaseOrder_DiscountAmount: totalDiscount,
      PurchaseOrder_TaxAmount: totalTax,
      PurchaseOrder_OtherCharges: otherCharges,
      PurchaseOrder_AdditionalDiscount: additionalDiscount,
      PurchaseOrder_NetAmount: netAmount, // Amount after discount + tax + other charges - additional discount
      PurchaseOrder_TotalQty: totalQty  // NEW: Set total quantity
    }));
  };

  const calculateDetailAmounts = (detailsArray, index) => {
    const detail = detailsArray[index];
    const quantity = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity) || 0;
    const unitPrice = parseFloat(detail.PurchaseOrderDet_UnitPrice) || 0;
    const discountPercent = parseFloat(detail.PurchaseOrderDet_DiscountPercent) || 0;
    const taxPercent = parseFloat(detail.PurchaseOrderDet_TaxPercent) || 0;

    // If qty is zero, entire line should be zero regardless of bonus
    if (quantity <= 0) {
      detailsArray[index] = {
        ...detail,
        PurchaseOrderDet_DiscountAmount: 0,
        PurchaseOrderDet_TotalAmount: 0,
        PurchaseOrderDet_TaxAmount: 0,
        PurchaseOrderDet_NetAmount: 0
      };
      return;
    }

    // 1) Calculate amount before discount (subtotal)
    const amountBeforeDiscount = roundHalfUp(quantity * unitPrice, 2);

    // 2) Discounted unit price (rounded first)
    const discountedUnitPrice = roundHalfUp(unitPrice * (1 - (discountPercent / 100)), 2);

    // 3) Discount amount on purchased qty only
    const discountAmount = roundHalfUp(quantity * unitPrice * (discountPercent / 100), 2);

    // 4) Line total amount after discount (qty × discounted unit)
    const lineTotalAmount = roundHalfUp(quantity * discountedUnitPrice, 2);

    // 5) Tax base on (qty + bonus) at discounted unit
    const totalQuantity = quantity + bonusQuantity;
    const taxBase = totalQuantity * discountedUnitPrice;

    // 6) Tax components (multiple taxes supported via TaxDetails); else fallback to taxPercent
    let taxComponentsSum = 0;
    if (Array.isArray(detail.TaxDetails) && detail.TaxDetails.length > 0) {
      taxComponentsSum = detail.TaxDetails.reduce((sum, t) => {
        const perc = parseFloat(t.Tax_Perc || t.tax_Perc || 0) || 0;
        const comp = roundHalfUp(taxBase * (perc / 100), 2);
        return sum + comp;
      }, 0);
    } else {
      taxComponentsSum = roundHalfUp(taxBase * (taxPercent / 100), 2);
    }
    const taxAmount = roundHalfUp(taxComponentsSum, 2);

    // 7) Net amount (amount after discount + tax)
    const netAmount = roundHalfUp(lineTotalAmount + taxAmount, 2);

    // Update detail with rounded values
    // Total Amount should show amount BEFORE discount for clarity
    // Net Amount shows amount AFTER discount + tax
    detailsArray[index] = {
      ...detail,
      PurchaseOrderDet_DiscountAmount: discountAmount,
      PurchaseOrderDet_TotalAmount: amountBeforeDiscount, // Changed: Now shows amount before discount
      PurchaseOrderDet_TaxAmount: taxAmount,
      PurchaseOrderDet_NetAmount: netAmount
    };
  };

  const addDetail = () => {
    // Validate that all products with selected items have stock details entered
    if (!isReturnMode) {
      for (let i = 0; i < details.length; i++) {
        const detail = details[i];
        // Only validate if product is selected
        if (detail.PurchaseOrderDet_Product_Id) {
          // Check if stock details exist and have sales price
          if (!detail.stockDetails || detail.stockDetails.length === 0) {
            Swal.fire({
              icon: 'error',
              title: 'Stock Details Required',
              text: `Please enter stock details (including sales price) for product "${detail.Product_NameEn || 'Unknown'}" before adding a new row.`,
              confirmButtonText: 'OK'
            }).then(() => {
              // Open stock details modal after alert is closed
              openStockModal(i);
            });
            return;
          }
          
          // Check if all stock details have sales price entered
          const missingSalesPrice = detail.stockDetails.find((stock, index) => {
            const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
            return salesPrice <= 0;
          });
          
          if (missingSalesPrice) {
            const stockIndex = detail.stockDetails.findIndex((stock) => {
              const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
              return salesPrice <= 0;
            });
            Swal.fire({
              icon: 'error',
              title: 'Sales Price Required',
              text: `Please enter a sales price greater than 0 for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}" before adding a new row.`,
              confirmButtonText: 'OK'
            }).then(() => {
              // Open stock details modal after alert is closed
              openStockModal(i);
            });
            return;
          }
        }
      }
    }
    
    setDetails([...details, { ...defaultDetail }]);
  };

  const removeDetail = (index) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      setDetails(newDetails);
    }
  };

  const openStockModal = async (index) => {
    console.log('=== OPEN STOCK MODAL DEBUG ===');
    console.log('Index:', index);
    console.log('Detail:', details[index]);
    
    const detail = details[index];
    const productId = getDetailField(detail, 'PurchaseOrderDet_Product_Id');
    
    console.log('Product ID found:', productId);
    console.log('Detail keys:', Object.keys(detail || {}));
    
    if (!detail || !productId) {
      console.log('No product ID found, showing error');
      Swal.fire("Error", "Please select a product first", "error");
      return;
    }

    const product = products.find(p => p.product_Id === productId);
    console.log('Product found:', product);
    setCurrentProduct(product);
    setCurrentDetailIndex(index);
    
    // If in return mode, use batch-based stock details modal (same as regular purchase)
    if (isReturnMode) {
      // Generate form ID when first product is opened in return mode
      if (!temporaryFormUniqueId) {
        const formId = generateTemporaryFormUniqueId();
        setTemporaryFormUniqueId(formId);
      }
      
      // For return mode, check if stock details already exist and are in batch format
      if (detail.stockDetails && detail.stockDetails.length > 0) {
        // Check if stock details are already in batch format (have batchQuantity or PurchaseStock_BatchQuantity)
        const firstItem = detail.stockDetails[0];
        const hasBatchQuantity = firstItem && (
          (firstItem.batchQuantity !== undefined && firstItem.batchQuantity !== null) || 
          (firstItem.PurchaseStock_BatchQuantity !== undefined && firstItem.PurchaseStock_BatchQuantity !== null)
        );
        
        if (hasBatchQuantity) {
          // Already in batch format, use as is but ensure batchQuantity, stockIds, originalStockIds, and IsBonus flag are preserved
          const batchesWithBatchQty = detail.stockDetails.map(batch => {
            const currentStockIds = batch.stockIds || [];
            // Get saved batchQuantity - this is the user-entered value, don't recalculate from stockIds
            // Handle 0 values correctly (0 is falsy but valid)
            const savedBatchQuantity = (batch.batchQuantity !== undefined && batch.batchQuantity !== null)
              ? parseFloat(batch.batchQuantity) || 0
              : ((batch.PurchaseStock_BatchQuantity !== undefined && batch.PurchaseStock_BatchQuantity !== null)
                ? parseFloat(batch.PurchaseStock_BatchQuantity) || 0
                : 0);
            
            // For originalStockIds: CRITICAL - this must be preserved to allow quantity changes
            // If missing, try multiple strategies to recover it
            let originalStockIds = batch.originalStockIds;
            
            if (!originalStockIds || originalStockIds.length === 0) {
              // Strategy 1: Use StockIds string if available (comma-separated, may contain all selected stock IDs)
              if (batch.StockIds && typeof batch.StockIds === 'string' && batch.StockIds.length > 0) {
                const idsFromString = batch.StockIds.split(',').filter(id => id.trim() !== '').map(id => parseInt(id.trim()));
                // Remove duplicates
                originalStockIds = [...new Set(idsFromString)];
              } 
              // Strategy 2: If currentStockIds length matches savedBatchQuantity, it might be the original
              else if (currentStockIds.length > 0 && currentStockIds.length === savedBatchQuantity && savedBatchQuantity > 0) {
                originalStockIds = [...currentStockIds]; // Create a copy
              }
              // Strategy 3: Last resort - use currentStockIds (data loss scenario)
              else if (currentStockIds.length > 0) {
                originalStockIds = [...currentStockIds]; // Create a copy
              } else {
                originalStockIds = [];
              }
            } else {
              // Ensure no duplicates in existing originalStockIds
              originalStockIds = [...new Set(originalStockIds)];
            }
            
            // Ensure originalStockIds is an array
            if (!Array.isArray(originalStockIds)) {
              originalStockIds = [];
            }
            
            return {
              ...batch,
              batchQuantity: savedBatchQuantity, // Use saved value, don't recalculate
              stockIds: currentStockIds, // Preserve stockIds array for return mode
              originalStockIds: originalStockIds, // Preserve original stockIds for quantity validation
              PurchaseStock_IsBonus: batch.PurchaseStock_IsBonus !== undefined ? batch.PurchaseStock_IsBonus : (batch.purchaseStock_IsBonus !== undefined ? batch.purchaseStock_IsBonus : false),
              purchaseStock_IsBonus: batch.purchaseStock_IsBonus !== undefined ? batch.purchaseStock_IsBonus : (batch.PurchaseStock_IsBonus !== undefined ? batch.PurchaseStock_IsBonus : false)
            };
          });
          setStockDetails(batchesWithBatchQty);
        } else {
          // Not in batch format, group existing stock items into batches
          // IMPORTANT: Store the original available stock count before grouping
          const originalAvailableCount = detail.stockDetails ? detail.stockDetails.length : 0;
          
          const batches = [];
          const stockMap = new Map();
          
          detail.stockDetails.forEach(stock => {
            // For return mode, group by batch number, expiry, sales price, AND IsBonus flag
            // This ensures regular and bonus stocks are kept in separate batches
            const isBonus = stock.PurchaseStock_IsBonus || stock.purchaseStock_IsBonus || false;
            const key = `${stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || ''}_${stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || ''}_${stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0}_${isBonus}`;
            
            if (stockMap.has(key)) {
              stockMap.get(key).batchQuantity += 1;
              // Keep track of stock IDs for return
              if (stock.PurchaseStock_Id || stock.purchaseStock_Id) {
                const stockId = stock.PurchaseStock_Id || stock.purchaseStock_Id;
                const batchData = stockMap.get(key);
                if (!batchData.stockIds) {
                  batchData.stockIds = [];
                }
                if (!batchData.originalStockIds) {
                  batchData.originalStockIds = [];
                }
                // Only add if not already present (prevent duplicates)
                if (!batchData.originalStockIds.includes(stockId)) {
                  batchData.originalStockIds.push(stockId);
                }
                if (!batchData.stockIds.includes(stockId)) {
                  batchData.stockIds.push(stockId);
                }
              }
            } else {
              const stockId = stock.PurchaseStock_Id || stock.purchaseStock_Id;
              const initialStockIds = stockId ? [stockId] : [];
              stockMap.set(key, {
                batchQuantity: 1,
                PurchaseStock_BatchNo: stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || '',
                PurchaseStock_ExpDate: stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || null,
                PurchaseStock_SalesPrice: stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0,
                PurchaseStock_CostPrice: stock.PurchaseStock_CostPrice || stock.purchaseStock_CostPrice || 0,
                PurchaseStock_IsBonus: isBonus, // Preserve IsBonus flag
                PurchaseStock_Id: stockId, // Store first stock ID for return mode
                stockIds: initialStockIds, // Array of stock IDs for this batch
                originalStockIds: initialStockIds // Preserve original stockIds for quantity validation
              });
            }
          });
          
          batches.push(...Array.from(stockMap.values()));
          
          // Store original available count in each batch for reference
          batches.forEach(batch => {
            batch._originalAvailableCount = originalAvailableCount;
          });
          
          setStockDetails(batches);
        }
      } else {
        // No existing stock, create empty batch
        const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
        const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
        const totalQuantity = quantity + bonusQuantity;
        
        const stockDetailsArray = [{
          ...defaultStock,
          batchQuantity: totalQuantity,
          PurchaseStock_IsBonus: false
        }];
        
        setStockDetails(stockDetailsArray);
      }
      
      // Fetch previous prices for the product (same as regular purchase)
      try {
        const prices = await getProductPreviousPrices(productId);
        setPreviousPrices(prices);
      } catch (error) {
        console.error('Error fetching previous prices:', error);
        setPreviousPrices(null);
      }
      
      setShowStockModal(true);
      return;
    }
    
    // For regular purchase, use existing stock details modal
    // Fetch previous prices for the product
    try {
      const prices = await getProductPreviousPrices(productId);
      console.log('=== PREVIOUS PRICES DEBUG ===');
      console.log('Raw API response:', prices);
      console.log('HasPreviousPrices:', prices?.hasPreviousPrices);
      console.log('PreviousSalesPrice:', prices?.previousSalesPrice);
      console.log('PreviousUnitPrice:', prices?.previousUnitPrice);
      setPreviousPrices(prices);
    } catch (error) {
      console.error('Error fetching previous prices:', error);
      setPreviousPrices(null);
    }
    
    // Check if stock details already exist for this product
    if (detail.stockDetails && detail.stockDetails.length > 0) {
      console.log('Loading existing stock details:', detail.stockDetails);
      
      // Check if stock details are already in batch format (have batchQuantity or PurchaseStock_BatchQuantity)
      const firstItem = detail.stockDetails[0];
      const hasBatchQuantity = firstItem && (
        (firstItem.batchQuantity !== undefined && firstItem.batchQuantity !== null) || 
        (firstItem.PurchaseStock_BatchQuantity !== undefined && firstItem.PurchaseStock_BatchQuantity !== null)
      );
      
      if (hasBatchQuantity) {
        // Already in batch format, use as is but ensure batchQuantity, stockIds, originalStockIds, and IsBonus flag are preserved
        const batchesWithBatchQty = detail.stockDetails.map(batch => {
          const currentStockIds = batch.stockIds || [];
          // Get saved batchQuantity - this is the user-entered value, don't recalculate from stockIds
          // Handle 0 values correctly (0 is falsy but valid)
          const savedBatchQuantity = (batch.batchQuantity !== undefined && batch.batchQuantity !== null)
            ? parseFloat(batch.batchQuantity) || 0
            : ((batch.PurchaseStock_BatchQuantity !== undefined && batch.PurchaseStock_BatchQuantity !== null)
              ? parseFloat(batch.PurchaseStock_BatchQuantity) || 0
              : 0);
          
          // For originalStockIds: CRITICAL - this must be preserved to allow quantity changes
          // If missing, try multiple strategies to recover it
          let originalStockIds = batch.originalStockIds;
          
          if (!originalStockIds || originalStockIds.length === 0) {
            // Strategy 1: Use StockIds string if available (comma-separated, may contain all selected stock IDs)
            if (batch.StockIds && typeof batch.StockIds === 'string' && batch.StockIds.length > 0) {
              originalStockIds = batch.StockIds.split(',').filter(id => id.trim() !== '').map(id => parseInt(id.trim()));
            } 
            // Strategy 2: If currentStockIds length matches savedBatchQuantity, it might be the original
            else if (currentStockIds.length > 0 && currentStockIds.length === savedBatchQuantity && savedBatchQuantity > 0) {
              originalStockIds = currentStockIds;
            }
            // Strategy 3: Last resort - use currentStockIds (data loss scenario)
            else if (currentStockIds.length > 0) {
              originalStockIds = currentStockIds;
            } else {
              originalStockIds = [];
            }
          }
          
          // Ensure originalStockIds is an array
          if (!Array.isArray(originalStockIds)) {
            originalStockIds = [];
          }
          
          return {
            ...batch,
            batchQuantity: savedBatchQuantity, // Use saved value, don't recalculate
            stockIds: currentStockIds, // Preserve stockIds array for return mode
            originalStockIds: originalStockIds, // Preserve original stockIds for quantity validation
            PurchaseStock_IsBonus: batch.PurchaseStock_IsBonus !== undefined ? batch.PurchaseStock_IsBonus : (batch.purchaseStock_IsBonus !== undefined ? batch.purchaseStock_IsBonus : false),
            purchaseStock_IsBonus: batch.purchaseStock_IsBonus !== undefined ? batch.purchaseStock_IsBonus : (batch.PurchaseStock_IsBonus !== undefined ? batch.PurchaseStock_IsBonus : false)
          };
        });
        setStockDetails(batchesWithBatchQty);
      } else {
        // Group existing individual stock details into batches (same logic as return mode for consistency)
        const batches = [];
        const stockMap = new Map();
        
        detail.stockDetails.forEach(stock => {
          // Group by batch number, expiry, sales price, AND IsBonus flag
          // This ensures regular and bonus stocks are kept in separate batches
          const isBonus = stock.PurchaseStock_IsBonus || stock.purchaseStock_IsBonus || false;
          const key = `${stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || ''}_${stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || ''}_${stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0}_${isBonus}`;
          
          if (stockMap.has(key)) {
            stockMap.get(key).batchQuantity += 1;
            // Also update PurchaseStock_BatchQuantity to match
            stockMap.get(key).PurchaseStock_BatchQuantity = stockMap.get(key).batchQuantity;
          } else {
            stockMap.set(key, {
              batchQuantity: 1,
              PurchaseStock_BatchQuantity: 1, // Include this field for batch format consistency
              PurchaseStock_BatchNo: stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || '',
              PurchaseStock_ExpDate: stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || null,
              PurchaseStock_SalesPrice: stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0,
              PurchaseStock_CostPrice: stock.PurchaseStock_CostPrice || stock.purchaseStock_CostPrice || 0,
              PurchaseStock_IsBonus: isBonus, // Preserve IsBonus flag
              purchaseStock_IsBonus: isBonus, // Include both variations for consistency
            });
          }
        });
        
        batches.push(...Array.from(stockMap.values()));
        setStockDetails(batches);
      }
    } else {
      console.log('Initializing new stock details');
      // Initialize new stock details with separate regular and bonus batches
      const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
      const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
      
      console.log('Quantity:', quantity, 'Bonus Quantity:', bonusQuantity);
      
      // Create separate batches for regular and bonus quantities
      const stockDetailsArray = [];
      
      if (quantity > 0) {
        stockDetailsArray.push({
          ...defaultStock,
          batchQuantity: quantity,
          PurchaseStock_IsBonus: false // Regular batch
        });
      }
      
      if (bonusQuantity > 0) {
        stockDetailsArray.push({
          ...defaultStock,
          batchQuantity: bonusQuantity,
          PurchaseStock_IsBonus: true // Bonus batch
        });
      }
      
      // If both are 0, create a single empty batch
      if (stockDetailsArray.length === 0) {
        stockDetailsArray.push({
          ...defaultStock,
          batchQuantity: 0,
          PurchaseStock_IsBonus: false
        });
      }
      
      console.log('Stock details array:', stockDetailsArray);
      setStockDetails(stockDetailsArray);
    }
    
    setShowStockModal(true);
  };

  const handleStockChange = (index, field, value) => {
    const newStockDetails = [...stockDetails];
    
    // Auto-format date for expiry date field
    if (field === 'PurchaseStock_ExpDate' && value) {
      let formattedValue = value.replace(/\D/g, ''); // Remove non-digits
      
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.substring(0, 2) + '/' + formattedValue.substring(2);
      }
      if (formattedValue.length >= 5) {
        formattedValue = formattedValue.substring(0, 5) + '/' + formattedValue.substring(5, 9);
      }
      
      newStockDetails[index] = { ...newStockDetails[index], [field]: formattedValue };
    } else if (field === 'batchQuantity') {
      // Handle batch quantity change
      let numValue = parseFloat(value) || 0;
      const currentBatch = newStockDetails[index];
      // Get original available stockIds (preserve even if currently trimmed)
      // Use originalStockIds if available, otherwise use current stockIds
      const originalStockIds = currentBatch.originalStockIds || currentBatch.stockIds || [];
      const currentStockIds = currentBatch.stockIds || [];
      
      if (isReturnMode) {
        // In return mode, adjust stockIds array to match the new quantity
        // But preserve originalStockIds to know the maximum available
        let adjustedStockIds = [...currentStockIds];
        const maxAvailable = originalStockIds.length;
        
        if (numValue === 0) {
          // Quantity set to 0: keep original stockIds for future use, but set adjusted to empty
          adjustedStockIds = [];
        } else if (numValue <= maxAvailable) {
          // Quantity is within available stock: use first numValue items from original stockIds
          adjustedStockIds = originalStockIds.slice(0, numValue);
        } else {
          // Quantity exceeds available: limit to available and show warning
          Swal.fire({
            icon: 'warning',
            title: 'Quantity Limit',
            text: `You can only return ${maxAvailable} items from this batch. Please select more stock items first if you need to return more.`,
            confirmButtonText: 'OK'
          });
          // Set quantity to available stock count
          adjustedStockIds = [...originalStockIds];
          numValue = maxAvailable;
        }
        
        newStockDetails[index] = { 
          ...currentBatch, 
          batchQuantity: numValue,
          stockIds: adjustedStockIds,
          originalStockIds: originalStockIds // Always preserve original for future reference
        };
      } else {
        // Regular purchase mode: just update quantity
        newStockDetails[index] = { 
          ...currentBatch, 
          batchQuantity: numValue,
          stockIds: currentStockIds  // Preserve stock IDs
        };
      }
    } else {
      newStockDetails[index] = { ...newStockDetails[index], [field]: value };
    }
    
    // Auto-fill subsequent rows for batch number, expiry date, and sales price
    if (field === 'PurchaseStock_BatchNo' || field === 'PurchaseStock_ExpDate' || field === 'PurchaseStock_SalesPrice') {
      for (let i = index + 1; i < newStockDetails.length; i++) {
        newStockDetails[i] = { ...newStockDetails[i], [field]: newStockDetails[index][field] };
      }
    }
    
    setStockDetails(newStockDetails);
  };

  const addNextBatch = (isBonus = false) => {
    const detail = details[currentDetailIndex];
    if (!detail) return;
    
    const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
    
    // Use functional update to ensure we have the latest stockDetails state
    setStockDetails(currentStockDetails => {
      // Calculate allocated quantity for the specific type (regular or bonus)
      const allocatedQuantity = currentStockDetails
        .filter(batch => {
          const batchIsBonus = batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus || false;
          return batchIsBonus === isBonus;
        })
        .reduce((sum, batch) => {
          const batchQty = parseFloat(batch.batchQuantity) || 0;
          return sum + batchQty;
        }, 0);
      
      // Get target quantity based on type
      const targetQuantity = isBonus ? bonusQuantity : quantity;
      const remainingQuantity = targetQuantity - allocatedQuantity;
      
      if (remainingQuantity <= 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Remaining Quantity',
          text: `All ${isBonus ? 'bonus' : 'regular'} quantity has been allocated to batches.`,
          confirmButtonText: 'OK'
        });
        return currentStockDetails; // Return unchanged state
      }
      
      // Get values from last batch of the same type for auto-fill (except quantity)
      const lastBatchOfType = [...currentStockDetails]
        .reverse()
        .find(batch => {
          const batchIsBonus = batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus || false;
          return batchIsBonus === isBonus;
        }) || {};
      
      const newBatch = {
        ...defaultStock,
        batchQuantity: remainingQuantity, // Auto-fill with remaining quantity for this type
        PurchaseStock_BatchNo: lastBatchOfType.PurchaseStock_BatchNo || '',
        PurchaseStock_ExpDate: lastBatchOfType.PurchaseStock_ExpDate || null,
        PurchaseStock_SalesPrice: lastBatchOfType.PurchaseStock_SalesPrice || 0,
        PurchaseStock_CostPrice: lastBatchOfType.PurchaseStock_CostPrice || 0,
        PurchaseStock_IsBonus: isBonus, // Set the type correctly
      };
      
      return [...currentStockDetails, newBatch];
    });
  };

  const saveStockDetails = () => {
    const detail = details[currentDetailIndex];
    if (!detail) {
      console.error("Detail not found at index:", currentDetailIndex);
      return;
    }
    
    const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
    const totalQuantity = quantity + bonusQuantity;
    
    // Validate batch quantities separately for regular and bonus
    const regularBatchQty = stockDetails
      .filter(batch => !(batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
      .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
    const bonusBatchQty = stockDetails
      .filter(batch => (batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
      .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
    
    // For return mode, skip quantity mismatch validation (user can return any quantity)
    // For regular purchase, validate that regular and bonus batch quantities match their respective quantities
    if (!isReturnMode) {
      if (regularBatchQty !== quantity) {
        Swal.fire({
          icon: 'error',
          title: 'Regular Quantity Mismatch',
          text: `Total regular batch quantity (${regularBatchQty}) must equal regular quantity (${quantity}). Please adjust the regular batch quantities.`,
          confirmButtonText: 'OK'
        });
        return;
      }
      
      if (bonusBatchQty !== bonusQuantity) {
        Swal.fire({
          icon: 'error',
          title: 'Bonus Quantity Mismatch',
          text: `Total bonus batch quantity (${bonusBatchQty}) must equal bonus quantity (${bonusQuantity}). Please adjust the bonus batch quantities.`,
          confirmButtonText: 'OK'
        });
        return;
      }
    }
    
    // Batches already have IsBonus flag set correctly (separate regular/bonus batches)
    // The validation above ensures that regular and bonus batch quantities match their respective quantities
    // So we should preserve the IsBonus flags as they were set when batches were created
    let finalBatches = stockDetails;
    
    // For return mode, validate that at least some quantity is being returned
    if (isReturnMode) {
      const totalBatchQuantity = stockDetails.reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
      if (totalBatchQuantity <= 0) {
        Swal.fire({
          icon: 'error',
          title: 'No Quantity to Return',
          text: 'Please enter a quantity greater than 0 for at least one batch to return.',
          confirmButtonText: 'OK'
        });
        return;
      }
    }
    
    // Validate that all batches have quantity > 0 (only for regular purchase, not return mode)
    // For return mode, user can have batches with 0 quantity (not returning from those batches)
    if (!isReturnMode) {
      const invalidBatch = stockDetails.find((batch, index) => {
        const batchQty = parseFloat(batch.batchQuantity) || 0;
        return batchQty <= 0;
      });
      
      if (invalidBatch) {
        const batchIndex = stockDetails.findIndex((batch) => {
          const batchQty = parseFloat(batch.batchQuantity) || 0;
          return batchQty <= 0;
        });
        Swal.fire({
          icon: 'error',
          title: 'Invalid Batch Quantity',
          text: `Batch ${batchIndex + 1} must have a quantity greater than 0.`,
          confirmButtonText: 'OK'
        });
        return;
      }
    }
    
    // Check if product requires expiry date
    const productExpiryRequired = currentProduct?.expiryRequired || detail?.expiryRequired || false;
    
    // Validate expiry date if required
    if (productExpiryRequired) {
      // Check if expiry date is missing
      const missingExpiryDate = stockDetails.find((batch, index) => {
        const expDate = batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || '';
        return !expDate || expDate.trim() === '';
      });
      
      if (missingExpiryDate) {
        const batchIndex = stockDetails.findIndex((batch) => {
          const expDate = batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || '';
          return !expDate || expDate.trim() === '';
        });
        Swal.fire({
          icon: 'error',
          title: 'Expiry Date Required',
          text: `Please enter an expiry date for batch ${batchIndex + 1}. This product requires an expiry date.`,
          confirmButtonText: 'OK'
        });
        return;
      }
      
      // Check if expiry date is valid
      const invalidExpiryDate = stockDetails.find((batch, index) => {
        const expDate = batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || '';
        if (!expDate || expDate.trim() === '') return false; // Already checked above
        return !isValidDate(expDate);
      });
      
      if (invalidExpiryDate) {
        const batchIndex = stockDetails.findIndex((batch) => {
          const expDate = batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || '';
          if (!expDate || expDate.trim() === '') return false;
          return !isValidDate(expDate);
        });
        Swal.fire({
          icon: 'error',
          title: 'Invalid Expiry Date',
          text: `Please enter a valid expiry date (DD/MM/YYYY format) for batch ${batchIndex + 1}.`,
          confirmButtonText: 'OK'
        });
        return;
      }
    }
    
    // Validate that all sales prices are entered (greater than 0)
    const missingSalesPrice = stockDetails.find((batch, index) => {
      const salesPrice = parseFloat(batch.PurchaseStock_SalesPrice || batch.purchaseStock_SalesPrice) || 0;
      return salesPrice <= 0;
    });
    
    if (missingSalesPrice) {
      const batchIndex = stockDetails.findIndex((batch) => {
        const salesPrice = parseFloat(batch.PurchaseStock_SalesPrice || batch.purchaseStock_SalesPrice) || 0;
        return salesPrice <= 0;
      });
      Swal.fire({
        icon: 'error',
        title: 'Sales Price Required',
        text: `Please enter a sales price greater than 0 for batch ${batchIndex + 1}.`,
        confirmButtonText: 'OK'
      });
      return;
    }
    
    const totalAmount = parseFloat(quantity * (detail.purchaseOrderDet_UnitPrice || detail.PurchaseOrderDet_UnitPrice)) || 0;
    
    console.log("=== SAVE STOCK DETAILS DEBUG ===");
    const totalBatchQty = stockDetails.reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
    console.log("Current Detail Index:", currentDetailIndex);
    console.log("Product ID:", detail.PurchaseOrderDet_Product_Id);
    console.log("Quantity:", quantity, "Bonus Qty:", bonusQuantity, "Total Qty:", totalQuantity);
    console.log("Total Batch Quantity:", totalBatchQty);
    console.log("Stock Details Batches:", stockDetails.length);
    console.log("Stock Details Data:", stockDetails);
    
    // Calculate cost price: total amount / total quantity (qty + bonus qty)
    const costPrice = totalQuantity > 0 ? totalAmount / totalQuantity : 0;
    
    // Use the batches with auto-assigned IsBonus flags
    const stockDetailsToProcess = finalBatches || stockDetails;
    
    // For return mode, send batches with stock IDs (comma-separated) - backend will expand them
    // For regular purchase, store batches directly (backend will expand them server-side)
    let finalStockDetails;
    
    if (isReturnMode) {
      // Return mode: Send batches with stock IDs as comma-separated string (backend will expand)
      // IMPORTANT: Filter out batches with 0 quantity to avoid backend counting them incorrectly
      const batchStockDetails = stockDetailsToProcess
        .filter(batch => {
          const batchQty = parseFloat(batch.batchQuantity) || 0;
          return batchQty > 0; // Only include batches with quantity > 0
        })
        .map((batch) => {
        const isBonus = batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus || false;
        const stockIds = batch.stockIds || [];
        
        // For return mode, use user-entered batchQuantity, but ensure stockIds matches
        // Use originalStockIds to know the maximum available
        const originalStockIds = batch.originalStockIds || stockIds;
        const maxAvailable = originalStockIds.length;
        let batchQty = parseFloat(batch.batchQuantity) || 0;
        let adjustedStockIds = [];
        
        if (batchQty === 0) {
          // Quantity is 0: no stock items selected
          adjustedStockIds = [];
        } else if (batchQty <= maxAvailable) {
          // Quantity is within available: use first batchQty items from original
          adjustedStockIds = originalStockIds.slice(0, batchQty);
        } else {
          // Quantity exceeds available: limit to available
          batchQty = maxAvailable;
          adjustedStockIds = [...originalStockIds];
        }
        
        // Convert adjusted stock IDs array to comma-separated string
        const stockIdsString = adjustedStockIds.length > 0 ? adjustedStockIds.join(',') : '';
        
        // Build batch object - don't include PurchaseStock_Id for batches (backend will use StockIds)
        const batchObj = {
          batchQuantity: batchQty,  // Use user-entered quantity (adjusted if needed)
          PurchaseStock_BatchQuantity: batchQty,  // Send user-entered quantity to backend
          PurchaseStock_BatchNo: batch.PurchaseStock_BatchNo || batch.purchaseStock_BatchNo || '',
          PurchaseStock_ExpDate: batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || null,
          PurchaseStock_SalesPrice: batch.PurchaseStock_SalesPrice || batch.purchaseStock_SalesPrice || 0,
          PurchaseStock_CostPrice: batch.PurchaseStock_CostPrice || batch.purchaseStock_CostPrice || costPrice,
          PurchaseStock_IsBonus: isBonus,
          PurchaseStock_Product_Id: detail.PurchaseOrderDet_Product_Id || detail.purchaseOrderDet_Product_Id,
          PurchaseStock_Unit_Id: detail.PurchaseOrderDet_Unit_Id || detail.PurchaseOrderDet_Unit_Id,
          StockIds: stockIdsString,  // Send adjusted stock IDs as comma-separated string for backend expansion
          stockIds: adjustedStockIds,  // Preserve adjusted stockIds array for frontend (modal reopening)
          originalStockIds: originalStockIds  // Preserve original stockIds for future quantity changes
        };
        
        // Don't include PurchaseStock_Id for batches - backend will expand from StockIds
        // Only include it if it's an individual item (not a batch)
        if (batchQty === 0 && batch.PurchaseStock_Id && batch.PurchaseStock_Id > 0) {
          batchObj.PurchaseStock_Id = batch.PurchaseStock_Id;
        }
        
        return batchObj;
      });
      
      finalStockDetails = batchStockDetails;
      console.log("Return Mode - Batch Stock Details Count:", batchStockDetails.length, "Total items in batches:", finalStockDetails.reduce((sum, b) => sum + (parseFloat(b.PurchaseStock_BatchQuantity || b.batchQuantity) || 0), 0));
    } else {
      // Regular purchase: Store batches directly (backend will expand them server-side)
      const batchStockDetails = stockDetailsToProcess.map((batch) => {
      const batchQty = parseFloat(batch.batchQuantity) || 0;
      const isBonus = batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus || false;
      
      return {
        batchQuantity: batchQty,  // Preserve batchQuantity for frontend (modal reopening)
        PurchaseStock_BatchQuantity: batchQty,  // Send batch quantity to backend for expansion
          PurchaseStock_BatchNo: batch.PurchaseStock_BatchNo || batch.purchaseStock_BatchNo || '',
          PurchaseStock_ExpDate: batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || null,
          PurchaseStock_SalesPrice: batch.PurchaseStock_SalesPrice || batch.purchaseStock_SalesPrice || 0,
          PurchaseStock_CostPrice: batch.PurchaseStock_CostPrice || batch.purchaseStock_CostPrice || costPrice,
          PurchaseStock_IsBonus: isBonus,
          PurchaseStock_Product_Id: detail.PurchaseOrderDet_Product_Id || detail.purchaseOrderDet_Product_Id,
          PurchaseStock_Unit_Id: detail.PurchaseOrderDet_Unit_Id || detail.purchaseOrderDet_Unit_Id,
      };
    });
    
      finalStockDetails = batchStockDetails;
      console.log("Regular Purchase - Batch Stock Details Count:", batchStockDetails.length);
    }
    
    console.log("Stock Details Sample:", finalStockDetails.slice(0, 5));
    
    // Store stock details in component state (NO API CALL)
    const newDetails = [...details];
    
    // Check if this detail already had stock details before saving (to determine if we should add a new row)
    const currentDetailBeforeSave = newDetails[currentDetailIndex];
    const hadExistingStockDetails = currentDetailBeforeSave.stockDetails && 
      Array.isArray(currentDetailBeforeSave.stockDetails) && 
      currentDetailBeforeSave.stockDetails.length > 0;
    
    // For return mode, update quantity and bonus quantity based on selected stock items
    if (isReturnMode) {
      let regularQty = 0;
      let bonusQty = 0;
      
      // Count quantities from batches - use actual stock count (stockIds.length) or PurchaseStock_BatchQuantity
      finalStockDetails.forEach(batch => {
        // For return mode, use PurchaseStock_BatchQuantity which now contains the actual stock count
        // This ensures quantities match the actual stock items being returned
        const batchQty = parseFloat(batch.PurchaseStock_BatchQuantity || batch.batchQuantity) || 0;
        if (batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus) {
          bonusQty += batchQty;
        } else {
          regularQty += batchQty;
        }
      });
      
      // Preserve original available stock count when saving
      const currentDetail = newDetails[currentDetailIndex];
      const originalAvailableCount = currentDetail.originalAvailableStockCount || 
        (currentDetail.stockDetails && Array.isArray(currentDetail.stockDetails) && 
         currentDetail.stockDetails.length > 0 && 
         (currentDetail.stockDetails[0].PurchaseStock_Id !== undefined || 
          currentDetail.stockDetails[0].purchaseStock_Id !== undefined) &&
         !(currentDetail.stockDetails[0].batchQuantity !== undefined || 
           currentDetail.stockDetails[0].PurchaseStock_BatchQuantity !== undefined))
        ? currentDetail.stockDetails.length 
        : (currentDetail.originalAvailableStockCount || 0);
      
      // Update detail with calculated quantities
      newDetails[currentDetailIndex] = {
        ...newDetails[currentDetailIndex],
        PurchaseOrderDet_Quantity: regularQty,
        PurchaseOrderDet_BonusQuantity: bonusQty,
        stockDetails: finalStockDetails,
        originalAvailableStockCount: originalAvailableCount
      };
      
      // Recalculate amounts for this detail
      calculateDetailAmounts(newDetails, currentDetailIndex);
    } else {
      // Preserve original available stock count for regular purchase too (if it exists)
      const currentDetail = newDetails[currentDetailIndex];
      const originalAvailableCount = currentDetail.originalAvailableStockCount;
      
      newDetails[currentDetailIndex] = {
        ...newDetails[currentDetailIndex],
        stockDetails: finalStockDetails,
        ...(originalAvailableCount && { originalAvailableStockCount: originalAvailableCount })
      };
    }
    
    // Only add a new empty row automatically if this is the first time saving stock details for this product
    // If the detail already had stock details, we're just updating, so don't add a new row
    if (!hadExistingStockDetails) {
      newDetails.push({
        ...defaultDetail,
        stockDetails: []
      });
    }
    
    setDetails(newDetails);
    recalculateTotalsFromDetails(newDetails);
    setShowStockModal(false);
  };

  // Save stock details to detail object when modal closes (so they persist)
  const handleStockModalClose = () => {
    const detail = details[currentDetailIndex];
    if (detail && stockDetails && stockDetails.length > 0) {
      // Save current stock details to the detail object so they persist when modal is reopened
      const newDetails = [...details];
      newDetails[currentDetailIndex] = {
        ...newDetails[currentDetailIndex],
        stockDetails: stockDetails, // Persist stock details even if modal is closed without saving
        // Preserve originalAvailableStockCount if it exists
        originalAvailableStockCount: detail.originalAvailableStockCount || 0
      };
      setDetails(newDetails);
    }
    setShowStockModal(false);
  };

  const calculateTotals = () => {
    // In view mode, keep API values
    if (isViewMode) {
      return;
    }

    // Calculate totals from details for both new and edit entries
    let totalAmount = 0; // Amount before discount
    let totalDiscount = 0;
    let totalTax = 0;
    
    details.forEach((detail, index) => {
      if (detail && (detail.PurchaseOrderDet_Product_Id || detail.purchaseOrderDet_Product_Id)) {
        // Handle both field name variations
        // TotalAmount now represents amount before discount
        const totalAmountValue = parseFloat(detail.PurchaseOrderDet_TotalAmount || detail.purchaseOrderDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseOrderDet_DiscountAmount || detail.purchaseOrderDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseOrderDet_TaxAmount || detail.purchaseOrderDet_TaxAmount) || 0;
        
        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
      }
    });
    
    // Round footer components
    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);

    const otherCharges = parseFloat(form.PurchaseOrder_OtherCharges) || 0;
    const additionalDiscount = parseFloat(form.PurchaseOrder_AdditionalDiscount) || 0;
    // Net Amount = (Total Amount - Total Discount) + Total Tax + Other Charges - Additional Discount
    const amountAfterDiscount = totalAmount - totalDiscount;
    const netAmount = roundHalfUp(amountAfterDiscount + totalTax + otherCharges - additionalDiscount, 2);
    
    setForm(prev => ({
      ...prev,
      PurchaseOrder_TotalAmount: totalAmount, // Amount before discount
      PurchaseOrder_DiscountAmount: totalDiscount,
      PurchaseOrder_TaxAmount: totalTax,
      PurchaseOrder_NetAmount: netAmount // Amount after discount + tax + other charges - additional discount
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [details, form.PurchaseOrder_OtherCharges, form.PurchaseOrder_AdditionalDiscount]);

  const handleSave = async () => {
    try {
      // Validation
      if (!form.PurchaseOrder_Business_Id) {
        Swal.fire("Error", "Please select a company", "error");
        return;
      }
      
      if (!form.PurchaseOrder_Supplier_Id) {
        Swal.fire("Error", "Please select a supplier", "error");
        return;
      }
      
      if (details.length === 0 || !details.some(detail => detail.PurchaseOrderDet_Product_Id)) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      // Remove empty rows (rows without product selected) before save
      const filteredDetails = details.filter(detail => detail.PurchaseOrderDet_Product_Id);
      if (filteredDetails.length === 0) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      // Validate quantity for regular purchase (must be > 0 and not empty)
      if (!isReturnMode) {
        const invalidQtyIndex = filteredDetails.findIndex(detail => {
          const qty = parseFloat(detail.PurchaseOrderDet_Quantity);
          return !qty || qty <= 0;
        });

        if (invalidQtyIndex !== -1) {
          Swal.fire({
            icon: 'error',
            title: 'Quantity Required',
            text: `Please enter a quantity greater than 0 for product row ${invalidQtyIndex + 1}.`,
            confirmButtonText: 'OK'
          });
          return;
        }

        // Validate quantity limits
        const qtyLimitExceededIndex = filteredDetails.findIndex(detail => {
          const qty = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
          return qty > 300000;
        });

        if (qtyLimitExceededIndex !== -1) {
          Swal.fire({
            icon: 'error',
            title: 'Quantity Limit Exceeded',
            text: `Quantity cannot exceed 300,000 for product row ${qtyLimitExceededIndex + 1}. Please reduce the quantity.`,
            confirmButtonText: 'OK'
          });
          return;
        }

        // Validate bonus quantity limits
        const bonusQtyLimitExceededIndex = filteredDetails.findIndex(detail => {
          const bonusQty = parseFloat(detail.PurchaseOrderDet_BonusQuantity) || 0;
          return bonusQty > 100000;
        });

        if (bonusQtyLimitExceededIndex !== -1) {
          Swal.fire({
            icon: 'error',
            title: 'Bonus Quantity Limit Exceeded',
            text: `Bonus quantity cannot exceed 100,000 for product row ${bonusQtyLimitExceededIndex + 1}. Please reduce the bonus quantity.`,
            confirmButtonText: 'OK'
          });
          return;
        }
      }
      
      // Validate stock details for all products (only for regular purchase mode)
      if (!isReturnMode) {
        for (let i = 0; i < filteredDetails.length; i++) {
          const detail = filteredDetails[i];
          
          // Check if stock details exist
          if (!detail.stockDetails || detail.stockDetails.length === 0) {
            // Find the original index in the details array (filteredDetails contains same object references)
            const originalIndex = details.findIndex(d => d === detail);
            
            Swal.fire({
              icon: 'error',
              title: 'Stock Details Required',
              text: `Please enter stock details (including sales price) for product "${detail.Product_NameEn || 'Unknown'}" before saving.`,
              confirmButtonText: 'OK'
            }).then(() => {
              // Open stock details modal after alert is closed
              if (originalIndex !== -1) {
                openStockModal(originalIndex);
              }
            });
            return;
          }
          
          // Check if all stock details have sales price entered
          const missingSalesPrice = detail.stockDetails.find((stock, index) => {
            const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
            return salesPrice <= 0;
          });
          
          if (missingSalesPrice) {
            const stockIndex = detail.stockDetails.findIndex((stock) => {
              const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
              return salesPrice <= 0;
            });
            // Find the original index in the details array (filteredDetails contains same object references)
            const originalIndex = details.findIndex(d => d === detail);
            
            Swal.fire({
              icon: 'error',
              title: 'Sales Price Required',
              text: `Please enter a sales price greater than 0 for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}" before saving.`,
              confirmButtonText: 'OK'
            }).then(() => {
              // Open stock details modal after alert is closed
              if (originalIndex !== -1) {
                openStockModal(originalIndex);
              }
            });
            return;
          }
          
          const productExpiryRequired = detail?.expiryRequired || false;
          
          if (productExpiryRequired && detail.stockDetails && detail.stockDetails.length > 0) {
            // Check if expiry date is missing
            const missingExpiryDate = detail.stockDetails.find((stock, index) => {
              const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
              return !expDate || expDate.trim() === '';
            });
            
            if (missingExpiryDate) {
              const stockIndex = detail.stockDetails.findIndex((stock) => {
                const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
                return !expDate || expDate.trim() === '';
              });
              Swal.fire({
                icon: 'error',
                title: 'Expiry Date Required',
                text: `Please enter an expiry date for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}". This product requires an expiry date.`,
                confirmButtonText: 'OK'
              });
              return;
            }
            
            // Check if expiry date is valid
            const invalidExpiryDate = detail.stockDetails.find((stock, index) => {
              const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
              if (!expDate || expDate.trim() === '') return false; // Already checked above
              return !isValidDate(expDate);
            });
            
            if (invalidExpiryDate) {
              const stockIndex = detail.stockDetails.findIndex((stock) => {
                const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
                if (!expDate || expDate.trim() === '') return false;
                return !isValidDate(expDate);
              });
              Swal.fire({
                icon: 'error',
                title: 'Invalid Expiry Date',
                text: `Please enter a valid expiry date (DD/MM/YYYY format) for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}".`,
                confirmButtonText: 'OK'
              });
              return;
            }
          }
        }
      }
      
      // Create a mapping from original index to filtered index for selectedStockForReturn
      const originalToFilteredIndexMap = new Map();
      let filteredIndex = 0;
      details.forEach((detail, originalIndex) => {
        if (detail.PurchaseOrderDet_Product_Id) {
          originalToFilteredIndexMap.set(originalIndex, filteredIndex);
          filteredIndex++;
        }
      });
      
      // Create filtered selectedStockForReturn mapping
      const filteredSelectedStockForReturn = {};
      Object.keys(selectedStockForReturn || {}).forEach(originalIndexStr => {
        const originalIndex = parseInt(originalIndexStr);
        const newIndex = originalToFilteredIndexMap.get(originalIndex);
        if (newIndex !== undefined) {
          filteredSelectedStockForReturn[newIndex] = selectedStockForReturn[originalIndex];
        }
      });
      
      // Update state to remove empty rows from UI
      setDetails(filteredDetails);
      if (isReturnMode) {
        setSelectedStockForReturn(filteredSelectedStockForReturn);
      }

      // For return mode, show confirmation
      if (isReturnMode) {
        const result = await Swal.fire({
          title: 'Confirm Purchase Return',
          text: 'Are you sure you want to create this purchase return?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#556ee6',
          cancelButtonColor: '#74788d',
          confirmButtonText: 'Yes, Create Return',
          cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      // Check stock locks before save (only for return mode)
      if (isReturnMode && temporaryFormUniqueId) {
        // Collect all selected stock IDs from detail.stockDetails
        const allStockIds = [];
        filteredDetails.forEach((detail) => {
          if (detail.PurchaseOrderDet_Product_Id && detail.stockDetails && detail.stockDetails.length > 0) {
            detail.stockDetails.forEach(stock => {
              const stockId = stock.PurchaseStock_Id || stock.purchaseStock_Id;
              if (stockId) {
                allStockIds.push(stockId.toString());
              }
            });
          }
        });
        
        if (allStockIds.length > 0) {
          try {
            const checkResult = await checkStockSelectionLocks(
              allStockIds.join(','),
              temporaryFormUniqueId
            );
            
            if (!checkResult.isSuccess || checkResult.hasLocks) {
              // Stocks are locked, prevent save and refresh stock modal
              Swal.fire({
                title: "Stock Locked",
                text: checkResult.message || "Some selected stocks are no longer available. Please refresh and select different stocks.",
                icon: "error",
                confirmButtonText: "OK"
              }).then(() => {
                // Refresh stock modal for the first detail with locked stock
                const firstDetailWithStock = filteredDetails.findIndex((detail) => 
                  detail.PurchaseOrderDet_Product_Id && detail.stockDetails && detail.stockDetails.length > 0
                );
                if (firstDetailWithStock >= 0) {
                  openStockModal(firstDetailWithStock);
                }
              });
              return;
            }
          } catch (error) {
            console.error('Error checking stock locks:', error);
            // Continue with save if check fails (don't block user)
          }
        }
      }

      setSaveLoading(true);
      const session = getUserSession();
      
      // Helper function to ensure StockIds is always a string (comma-separated)
      const ensureStockIdsString = (stockIds) => {
        if (!stockIds) return '';
        if (typeof stockIds === 'string') return stockIds;
        if (Array.isArray(stockIds)) return stockIds.join(',');
        return String(stockIds);
      };

      // Prepare stock details for save
      let stockDetailsForSave = [];
      if (isReturnMode) {
        // For return mode, use batches with stock IDs (already in batch format from saveStockDetails)
        filteredDetails.forEach((detail) => {
          if (detail.PurchaseOrderDet_Product_Id && detail.stockDetails && detail.stockDetails.length > 0) {
            // detail.stockDetails already contains batches with StockIds (comma-separated)
            detail.stockDetails.forEach(batch => {
              // Build stock detail object - only include PurchaseStock_Id if it's an individual item (not a batch)
              const stockDetail = {
                PurchaseStock_Product_Id: detail.PurchaseOrderDet_Product_Id,
                PurchaseStock_Unit_Id: detail.PurchaseOrderDet_Unit_Id,
                PurchaseStock_BatchQuantity: batch.PurchaseStock_BatchQuantity || parseFloat(batch.batchQuantity) || 0,
                PurchaseStock_BatchNo: batch.PurchaseStock_BatchNo || batch.purchaseStock_BatchNo || '',
                PurchaseStock_ExpDate: batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || null,
                PurchaseStock_SalesPrice: batch.PurchaseStock_SalesPrice || batch.purchaseStock_SalesPrice || 0,
                PurchaseStock_CostPrice: batch.PurchaseStock_CostPrice || batch.purchaseStock_CostPrice || detail.PurchaseOrderDet_UnitPrice || 0,
                PurchaseStock_IsBonus: batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus || false,
                StockIds: ensureStockIdsString(batch.StockIds || batch.stockIds)  // Ensure it's always a string
              };
              
              // Only include PurchaseStock_Id if it's an individual item (not a batch with StockIds)
              // For batches, backend will use StockIds to expand
              const batchQty = batch.PurchaseStock_BatchQuantity || parseFloat(batch.batchQuantity) || 0;
              const hasStockIds = batch.StockIds || (batch.stockIds && Array.isArray(batch.stockIds) && batch.stockIds.length > 0);
              if (batch.PurchaseStock_Id && batch.PurchaseStock_Id > 0 && batchQty === 0 && !hasStockIds) {
                stockDetail.PurchaseStock_Id = batch.PurchaseStock_Id;
              }
              
              stockDetailsForSave.push(stockDetail);
            });
          }
        });
      } else {
        // For regular purchase, use existing logic (batches with PurchaseStock_BatchQuantity)
        stockDetailsForSave = filteredDetails
          .filter(detail => detail.PurchaseOrderDet_Product_Id && detail.stockDetails)
          .flatMap(detail => {
            // Ensure StockIds is a string for regular purchases too
            return (detail.stockDetails || []).map(stock => ({
              ...stock,
              StockIds: ensureStockIdsString(stock.StockIds || stock.stockIds)
            }));
          });
      }
      
      // Calculate total quantity from filtered details (qty + bonus qty)
      const totalQty = filteredDetails.reduce((sum, detail) => {
        const quantity = parseFloat(detail.PurchaseOrderDet_Quantity || detail.purchaseOrderDet_Quantity) || 0;
        const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity || detail.purchaseOrderDet_BonusQuantity) || 0;
        return sum + quantity + bonusQuantity;
      }, 0);
      
      // Clean up Details - remove or properly format nested stockDetails to avoid deserialization issues
      const cleanedDetails = filteredDetails.map(detail => {
        const cleanedDetail = { ...detail };
        // Remove PurchaseOrderDet_Id if it's null, 0, or undefined (insert operation)
        if (!cleanedDetail.PurchaseOrderDet_Id || cleanedDetail.PurchaseOrderDet_Id === 0) {
          delete cleanedDetail.PurchaseOrderDet_Id;
        }
        // Format nested stockDetails/StockDetails to ensure StockIds is always a string
        // Backend model allows StockDetails as navigation property, so we format it correctly
        if (cleanedDetail.stockDetails || cleanedDetail.StockDetails) {
          const stockDetailsToFormat = cleanedDetail.stockDetails || cleanedDetail.StockDetails || [];
          // Format nested stockDetails to ensure StockIds is always a string
          cleanedDetail.StockDetails = stockDetailsToFormat.map(stock => {
            const formattedStock = { ...stock };
            // Ensure StockIds is always a string
            if (formattedStock.StockIds !== undefined || formattedStock.stockIds !== undefined) {
              formattedStock.StockIds = ensureStockIdsString(formattedStock.StockIds || formattedStock.stockIds);
              // Remove lowercase version if it exists
              delete formattedStock.stockIds;
            }
            return formattedStock;
          });
          // Remove the lowercase version to avoid confusion
          delete cleanedDetail.stockDetails;
        }
        return cleanedDetail;
      });
      
      // Prepare save data - remove ID field for inserts
      const saveData = {
        ...form,
        PurchaseOrder_InvoiceNo: form.PurchaseOrder_InvoiceNo,
        PurchaseOrder_CreatedBy: isEditing ? form.PurchaseOrder_CreatedBy : session.userId,
        PurchaseOrder_TotalQty: totalQty,  // Calculate and set total quantity
        IsReturn: isReturnMode ? true : false,
        Return_Purchase_Order_Id: isReturnMode ? returnPurchaseOrderId : null,
        Details: cleanedDetails,  // Use cleaned details without nested stockDetails issues
        StockDetails: stockDetailsForSave,
        TaxDetails: filteredDetails
          .filter(detail => detail.TaxDetails && detail.TaxDetails.length > 0)
          .flatMap((detail, index) => {
            const mappedTaxes = detail.TaxDetails.map(tax => {
              // Ensure correct field name for Purchase Order tax details
              const taxDetail = {
                TempDetailId: index + 1, // Use index + 1 as TempDetailId
                PurchaseOrderTaxDet_Tax_Id: tax.PurchaseOrderTaxDet_Tax_Id || tax.purchaseRequestTaxDet_Tax_Id || tax.PurchaseRequestTaxDet_Tax_Id || tax.tax_Id || tax.Tax_Id,
                Tax_NameEn: tax.Tax_NameEn || tax.tax_NameEn,
                Tax_Perc: tax.Tax_Perc || tax.tax_Perc
              };
              // Only include PurchaseOrderTaxDet_Id if it exists and is > 0 (update operation)
              if (tax.PurchaseOrderTaxDet_Id && tax.PurchaseOrderTaxDet_Id > 0) {
                taxDetail.PurchaseOrderTaxDet_Id = tax.PurchaseOrderTaxDet_Id;
              }
              return taxDetail;
            });
            return mappedTaxes;
          })
      };
      
      // Remove PurchaseOrder_Id if it's null, 0, or undefined (insert operation)
      // Backend cannot accept null for Int32, so we must delete the field completely
      if (!saveData.PurchaseOrder_Id || saveData.PurchaseOrder_Id === 0) {
        delete saveData.PurchaseOrder_Id;
      }
      
      // Additional cleanup: Remove any identity column ID fields that are 0 or null
      // This is a safety measure to prevent accidental sending of 0 for identity columns
      // Only target known identity columns, not foreign keys
      const identityColumnPatterns = ['PurchaseOrder_Id', 'PurchaseOrderDet_Id', 'PurchaseOrderTaxDet_Id'];
      identityColumnPatterns.forEach(pattern => {
        if (saveData[pattern] !== undefined && (saveData[pattern] === 0 || saveData[pattern] === null)) {
          delete saveData[pattern];
        }
      });

      // Validate at least one selected stock in return mode
      if (isReturnMode && stockDetailsForSave.length === 0) {
        Swal.fire("Error", "Please select at least one stock item to return.", "error");
        setSaveLoading(false);
        return;
      }

      // Validate: If Purchase Order is selected, mark as received must be checked
      if (selectedPurchaseOrderId && !markAsReceived) {
        Swal.fire({
          icon: 'warning',
          title: 'Mark as Received Required',
          text: 'Please check "Mark Purchase Order as Received" checkbox before saving, as this Purchase is based on a Purchase Order.',
          confirmButtonText: 'OK'
        });
        setSaveLoading(false);
        return;
      }

      const result = await savePurchaseOrder(saveData);
      
      if (result && (result.id > 0 || result.PurchaseOrder_Id > 0)) {
        // Mark Purchase Order as received if checkbox is checked
        if (markAsReceived && selectedPurchaseOrderId) {
          try {
            await markPurchaseRequestAsReceived(selectedPurchaseOrderId);
          } catch (error) {
            console.error("Error marking purchase order as received:", error);
            // Don't block the success message, just log the error
          }
        }
        
        // Cleanup stock locks after successful save (only for return mode)
        if (isReturnMode && temporaryFormUniqueId) {
          try {
            await deleteStockSelectionLock(temporaryFormUniqueId);
          } catch (error) {
            console.error('Error cleaning up stock locks after save:', error);
          }
        }
        
        Swal.fire("Success", isReturnMode ? "Purchase return created successfully!" : "Purchase saved successfully!", "success");
        // Set loading to prevent rendering during reset
        setSaveLoading(true);
        // Clear the form and reset to initial state
        setForm({ 
          ...defaultForm,
          PurchaseOrder_Date: new Date(),
          PurchaseOrder_DueDate: new Date()
        });
        setDetails([{ ...defaultDetail }]);
        setIsEditing(false);
        setShowEditForm(false);
        // Reset form ID
        setTemporaryFormUniqueId(null);
        setIsReturnMode(false);
        setReturnPurchaseOrderId(null);
        setOriginalInvoiceNo(null);
        setOriginalPurchaseOrderForReturn(null);
        setSelectedStockForReturn({});
        setPreloadedStockItems(null);
        // Clear Purchase Order selection
        setSelectedPurchaseOrderId(null);
        setSelectedPurchaseOrderData(null);
        setOriginalPurchaseOrderQuantities({});
        setMarkAsReceived(false);
        // Clear supplier dropdown state
        setSelectedSupplierOption(null);
        setSupplierSearchOptions([]);
        // Increment form key to force re-render of all Select components
        setFormKey(prev => prev + 1);
        // Add a delay to ensure state is reset before loading
        setTimeout(() => {
          loadPurchaseOrders();
          setSaveLoading(false);
        }, 200);
      } else {
        // Handle validation errors from backend
        const errorMessage = result?.message || "Failed to save purchase";
        Swal.fire("Error", errorMessage, "error");
      }
    } catch (error) {
      console.error("Error saving purchase:", error);
      // Try to extract error message from response
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "Failed to save purchase";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = async (purchaseOrder) => {
    try {
      setLoading(true);
      // Fetch full purchase order data with stock details for edit mode
      const editFilter = {
        PurchaseOrder_Id: purchaseOrder.purchaseOrder_Id || purchaseOrder.PurchaseOrder_Id,
        IncludeStockDetails: true  // Include stock details for edit mode
      };
      const fullPurchaseOrderData = await getPurchaseOrders(editFilter);
      const fullPurchaseOrder = fullPurchaseOrderData && fullPurchaseOrderData.length > 0 
        ? fullPurchaseOrderData[0] 
        : purchaseOrder; // Fallback to original if fetch fails
      
      setForm({
        PurchaseOrder_Id: fullPurchaseOrder.purchaseOrder_Id,
        PurchaseOrder_InvoiceNo: fullPurchaseOrder.purchaseOrder_InvoiceNo,
        PurchaseOrder_RefNo: fullPurchaseOrder.purchaseOrder_RefNo,
        PurchaseOrder_Supplier_Id: fullPurchaseOrder.purchaseOrder_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: fullPurchaseOrder.purchaseOrder_InvoiceType_Id,
        PurchaseOrder_Date: fullPurchaseOrder.purchaseOrder_Date ? new Date(fullPurchaseOrder.purchaseOrder_Date) : new Date(),
        PurchaseOrder_DueDate: fullPurchaseOrder.purchaseOrder_DueDate ? new Date(fullPurchaseOrder.purchaseOrder_DueDate) : new Date(),
        PurchaseOrder_Amount: fullPurchaseOrder.purchaseOrder_Amount,
        PurchaseOrder_DiscountPercent: fullPurchaseOrder.purchaseOrder_DiscountPercent,
        PurchaseOrder_DiscountAmount: fullPurchaseOrder.purchaseOrder_DiscountAmount,
        PurchaseOrder_OtherAmount: fullPurchaseOrder.purchaseOrder_OtherAmount,
        PurchaseOrder_TaxAmount: fullPurchaseOrder.purchaseOrder_TaxAmount,
        PurchaseOrder_OtherCharges: fullPurchaseOrder.purchaseOrder_OtherCharges,
        PurchaseOrder_AdditionalDiscount: fullPurchaseOrder.purchaseOrder_AdditionalDiscount,
        PurchaseOrder_NetAmount: fullPurchaseOrder.purchaseOrder_NetAmount,
        PurchaseOrder_Notes: fullPurchaseOrder.purchaseOrder_Notes,
        PurchaseOrder_TotalQty: fullPurchaseOrder.purchaseOrder_TotalQty,
        PurchaseOrder_Branch_Id: fullPurchaseOrder.purchaseOrder_Branch_Id,
        PurchaseOrder_TotalAmount: fullPurchaseOrder.purchaseOrder_TotalAmount,
        PurchaseOrder_CreatedDate: fullPurchaseOrder.purchaseOrder_CreatedDate,
        PurchaseOrder_CreatedBy: fullPurchaseOrder.purchaseOrder_CreatedBy,
        PurchaseOrder_Business_Id: fullPurchaseOrder.purchaseOrder_Business_Id,
        Supplier_NameEn: fullPurchaseOrder.supplier_NameEn,
        InvoiceType_Name: fullPurchaseOrder.invoiceType_Name
      });
      
      // Set selected supplier option for display
      if (fullPurchaseOrder.purchaseOrder_Supplier_Id && fullPurchaseOrder.supplier_NameEn) {
        // Look up supplier from loaded suppliers list to get the code
        const supplier = suppliers.find(s => s.supplier_Id === fullPurchaseOrder.purchaseOrder_Supplier_Id);
        const supplierCode = supplier?.supplier_Code || fullPurchaseOrder.supplier_Code || fullPurchaseOrder.supplier_ContactNo || '';
        setSelectedSupplierOption({
          value: fullPurchaseOrder.purchaseOrder_Supplier_Id,
          label: `${fullPurchaseOrder.supplier_NameEn} — ${supplierCode}`
        });
      } else {
        setSelectedSupplierOption(null);
      }
      
      
      if (fullPurchaseOrder.details && fullPurchaseOrder.details.length > 0) {
        // Convert API field names to form field names and recalculate amounts using new rules
        const mappedDetails = fullPurchaseOrder.details.map((detail) => ({
          PurchaseOrderDet_Id: detail.purchaseOrderDet_Id,
          PurchaseOrderDet_Product_Id: detail.purchaseOrderDet_Product_Id,
          PurchaseOrderDet_Unit_Id: detail.purchaseOrderDet_Unit_Id,
          PurchaseOrderDet_Quantity: detail.purchaseOrderDet_Quantity,
          PurchaseOrderDet_BonusQuantity: detail.purchaseOrderDet_BonusQuantity,
          PurchaseOrderDet_UnitPrice: detail.purchaseOrderDet_UnitPrice,
          PurchaseOrderDet_DiscountPercent: detail.purchaseOrderDet_DiscountPercent,
          PurchaseOrderDet_DiscountAmount: 0,
          PurchaseOrderDet_TotalAmount: 0,
          PurchaseOrderDet_NetAmount: 0,
          PurchaseOrderDet_TaxPercent: detail.purchaseOrderDet_TaxPercent,
          PurchaseOrderDet_TaxAmount: 0,
          Product_NameEn: detail.product_NameEn,
          Unit_NameEn: detail.unit_NameEn,
          TaxDetails: detail.taxDetails || [],
          stockDetails: detail.stockDetails || []
        }));

        // Recalculate for each detail with new logic
        const calcDetails = [...mappedDetails];
        for (let i = 0; i < calcDetails.length; i++) {
          calculateDetailAmounts(calcDetails, i);
        }
        setDetails(calcDetails);
        // Also refresh footer totals after mapping
        recalculateTotalsFromDetails(calcDetails);
      } else {
        setDetails([{ ...defaultDetail }]);
      }
      
      setIsEditing(true);
      setIsViewMode(false);
      setShowEditForm(true);
      setActiveTab("2"); // Keep on list tab, show edit form
      scrollToFormTop();
    } catch (error) {
      console.error("Error loading purchase order for edit:", error);
      Swal.fire("Error", "Failed to load purchase order details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Load Purchase Orders for selected supplier
  const loadPurchaseOrdersForSupplier = async (supplierId) => {
    if (!supplierId) {
      setPurchaseOrdersList([]);
      return;
    }

    try {
      setLoadingPurchaseOrders(true);
      const filter = {
        PurchaseRequest_Supplier_Id: supplierId,
        PurchaseRequest_Is_Received: false  // Only unreceived orders
      };
      
      const data = await getPurchaseRequests(filter);
      setPurchaseOrdersList(data || []);
      
      // Auto-open modal if there are Purchase Orders available
      if (data && data.length > 0) {
        setShowPurchaseOrderModal(true);
      } else {
        // Don't show alert on auto-load, only when user clicks icon
      }
    } catch (error) {
      console.error("Error loading purchase orders for supplier:", error);
      setPurchaseOrdersList([]);
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  // Handle Purchase Order selection from modal
  const handlePurchaseOrderSelect = async (purchaseOrder) => {
    try {
      setLoadingPurchaseOrders(true);
      
      // Store the selected Purchase Order data
      const poId = purchaseOrder.purchaseRequest_Id || purchaseOrder.PurchaseRequest_Id;
      setSelectedPurchaseOrderId(poId);
      setSelectedPurchaseOrderData(purchaseOrder);
      
      // Map Purchase Order fields to Purchase form (handle both camelCase and PascalCase)
      setForm(prev => ({
        ...prev,
        PurchaseOrder_RefNo: purchaseOrder.purchaseRequest_RefNo || purchaseOrder.PurchaseRequest_RefNo || "",
        PurchaseOrder_Supplier_Id: purchaseOrder.purchaseRequest_Supplier_Id || purchaseOrder.PurchaseRequest_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: purchaseOrder.purchaseRequest_InvoiceType_Id || purchaseOrder.PurchaseRequest_InvoiceType_Id || 1,
        PurchaseOrder_Date: (purchaseOrder.purchaseRequest_Date || purchaseOrder.PurchaseRequest_Date) ? new Date(purchaseOrder.purchaseRequest_Date || purchaseOrder.PurchaseRequest_Date) : new Date(),
        PurchaseOrder_DueDate: (purchaseOrder.purchaseRequest_DueDate || purchaseOrder.PurchaseRequest_DueDate) ? new Date(purchaseOrder.purchaseRequest_DueDate || purchaseOrder.PurchaseRequest_DueDate) : new Date(),
        PurchaseOrder_Branch_Id: purchaseOrder.purchaseRequest_Branch_Id || purchaseOrder.PurchaseRequest_Branch_Id,
        PurchaseOrder_Business_Id: purchaseOrder.purchaseRequest_Business_Id || purchaseOrder.PurchaseRequest_Business_Id,
        PurchaseOrder_Notes: purchaseOrder.purchaseRequest_Notes || purchaseOrder.PurchaseRequest_Notes || "",
        PurchaseOrder_TotalAmount: purchaseOrder.purchaseRequest_TotalAmount || purchaseOrder.PurchaseRequest_TotalAmount || 0,
        PurchaseOrder_DiscountAmount: purchaseOrder.purchaseRequest_DiscountAmount || purchaseOrder.PurchaseRequest_DiscountAmount || 0,
        PurchaseOrder_TaxAmount: purchaseOrder.purchaseRequest_TaxAmount || purchaseOrder.PurchaseRequest_TaxAmount || 0,
        PurchaseOrder_NetAmount: purchaseOrder.purchaseRequest_NetAmount || purchaseOrder.PurchaseRequest_NetAmount || 0,
        PurchaseOrder_Amount: purchaseOrder.purchaseRequest_Amount || purchaseOrder.PurchaseRequest_Amount || 0,
        PurchaseOrder_DiscountPercent: purchaseOrder.purchaseRequest_DiscountPercent || purchaseOrder.PurchaseRequest_DiscountPercent || 0,
        PurchaseOrder_OtherAmount: purchaseOrder.purchaseRequest_OtherAmount || purchaseOrder.PurchaseRequest_OtherAmount || 0,
      }));

      // Set supplier option for display
      const supplierId = purchaseOrder.purchaseRequest_Supplier_Id || purchaseOrder.PurchaseRequest_Supplier_Id;
      const supplierName = purchaseOrder.supplier_NameEn || purchaseOrder.Supplier_NameEn;
      if (supplierId && supplierName) {
        const supplier = suppliers.find(s => s.supplier_Id === supplierId);
        const supplierCode = supplier?.supplier_Code || purchaseOrder.supplier_Code || purchaseOrder.supplier_ContactNo || '';
        setSelectedSupplierOption({
          value: supplierId,
          label: `${supplierName} — ${supplierCode}`
        });
      }

      // Generate new invoice number if business is selected
      const businessId = purchaseOrder.purchaseRequest_Business_Id || purchaseOrder.PurchaseRequest_Business_Id;
      if (businessId) {
        try {
          const result = await getNextInvoiceNo(businessId, false, null);
          if (result.nextInvoiceNo) {
            setForm(prev => ({
              ...prev,
              PurchaseOrder_InvoiceNo: result.nextInvoiceNo
            }));
          }
        } catch (error) {
          console.error("Error getting next invoice number:", error);
        }
      }

      // Map Purchase Order details to Purchase details
      if (purchaseOrder.details && purchaseOrder.details.length > 0) {
        const mappedDetails = purchaseOrder.details.map((detail) => {
          // Handle both camelCase and PascalCase field names
          const qty = detail.purchaseRequestDet_Quantity || detail.PurchaseRequestDet_Quantity || 0;
          const bonusQty = detail.purchaseRequestDet_BonusQuantity || detail.PurchaseRequestDet_BonusQuantity || 0;
          const productId = detail.purchaseRequestDet_Product_Id || detail.PurchaseRequestDet_Product_Id;
          const unitId = detail.purchaseRequestDet_Unit_Id || detail.PurchaseRequestDet_Unit_Id;
          const unitPrice = detail.purchaseRequestDet_UnitPrice || detail.PurchaseRequestDet_UnitPrice || 0;
          const discountPercent = detail.purchaseRequestDet_DiscountPercent || detail.PurchaseRequestDet_DiscountPercent || 0;
          const taxPercent = detail.purchaseRequestDet_TaxPercent || detail.PurchaseRequestDet_TaxPercent || 0;
          const productName = detail.product_NameEn || detail.Product_NameEn;
          const unitName = detail.unit_NameEn || detail.Unit_NameEn;
          const taxDetailsRaw = detail.taxDetails || detail.TaxDetails || [];
          
          // Map Purchase Request tax details to Purchase Order tax details format
          const taxDetails = taxDetailsRaw.map(tax => ({
            PurchaseOrderTaxDet_Tax_Id: tax.purchaseRequestTaxDet_Tax_Id || tax.PurchaseRequestTaxDet_Tax_Id || tax.tax_Id || tax.Tax_Id,
            Tax_NameEn: tax.tax_NameEn || tax.Tax_NameEn,
            Tax_Perc: tax.tax_Perc || tax.Tax_Perc,
            // Keep original ID for reference if needed
            purchaseRequestTaxDet_Id: tax.purchaseRequestTaxDet_Id || tax.PurchaseRequestTaxDet_Id,
            purchaseRequestTaxDet_PurchaseRequestDet_Id: tax.purchaseRequestTaxDet_PurchaseRequestDet_Id || tax.PurchaseRequestTaxDet_PurchaseRequestDet_Id
          }));
          
          // Store original quantities for comparison
          const originalQty = qty;
          const originalBonusQty = bonusQty;
          
          return {
            PurchaseOrderDet_Id: null,
            PurchaseOrderDet_Product_Id: productId,
            PurchaseOrderDet_Unit_Id: unitId,
            PurchaseOrderDet_Quantity: originalQty,
            PurchaseOrderDet_BonusQuantity: originalBonusQty,
            PurchaseOrderDet_UnitPrice: unitPrice,
            PurchaseOrderDet_DiscountPercent: discountPercent,
            PurchaseOrderDet_DiscountAmount: 0,
            PurchaseOrderDet_TotalAmount: 0,
            PurchaseOrderDet_NetAmount: 0,
            PurchaseOrderDet_TaxPercent: taxPercent,
            PurchaseOrderDet_TaxAmount: 0,
            Product_NameEn: productName,
            Unit_NameEn: unitName,
            TaxDetails: taxDetails,
            stockDetails: []  // No stock details from Purchase Order
          };
        });

        // Store original quantities for comparison
        const quantitiesMap = {};
        purchaseOrder.details.forEach((detail, index) => {
          quantitiesMap[index] = {
            quantity: detail.purchaseRequestDet_Quantity || detail.PurchaseRequestDet_Quantity || 0,
            bonusQuantity: detail.purchaseRequestDet_BonusQuantity || detail.PurchaseRequestDet_BonusQuantity || 0
          };
        });
        setOriginalPurchaseOrderQuantities(quantitiesMap);

        // Recalculate amounts for each detail
        const calcDetails = [...mappedDetails];
        for (let i = 0; i < calcDetails.length; i++) {
          calculateDetailAmounts(calcDetails, i);
        }
        setDetails(calcDetails);
        
        // Recalculate totals
        recalculateTotalsFromDetails(calcDetails);
        
        // Check if quantities match (auto-check mark as received)
        checkQuantitiesMatch(calcDetails, quantitiesMap);
      } else {
        setDetails([{ ...defaultDetail }]);
        setOriginalPurchaseOrderQuantities({});
        setMarkAsReceived(false);
      }

      // Close modal
      setShowPurchaseOrderModal(false);
    } catch (error) {
      console.error("Error selecting purchase order:", error);
      Swal.fire("Error", "Failed to load purchase order details", "error");
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  // Check if current quantities match original Purchase Order quantities
  const checkQuantitiesMatch = (currentDetails, originalQuantities) => {
    if (!originalQuantities || Object.keys(originalQuantities).length === 0) {
      setMarkAsReceived(false);
      return;
    }

    let allMatch = true;
    for (let i = 0; i < currentDetails.length; i++) {
      const currentDetail = currentDetails[i];
      const original = originalQuantities[i];
      
      if (!original) {
        allMatch = false;
        break;
      }

      const currentQty = parseFloat(currentDetail.PurchaseOrderDet_Quantity) || 0;
      const currentBonusQty = parseFloat(currentDetail.PurchaseOrderDet_BonusQuantity) || 0;
      
      if (currentQty !== original.quantity || currentBonusQty !== original.bonusQuantity) {
        allMatch = false;
        break;
      }
    }

    setMarkAsReceived(allMatch);
  };

  // Monitor details changes to update markAsReceived checkbox
  useEffect(() => {
    if (selectedPurchaseOrderId && Object.keys(originalPurchaseOrderQuantities).length > 0) {
      checkQuantitiesMatch(details, originalPurchaseOrderQuantities);
    }
  }, [details, selectedPurchaseOrderId, originalPurchaseOrderQuantities]);

  const handleView = async (purchaseOrder) => {
    try {
      setLoading(true);
      // Fetch full purchase order data with stock details for view mode
      const viewFilter = {
        PurchaseOrder_Id: purchaseOrder.purchaseOrder_Id || purchaseOrder.PurchaseOrder_Id,
        IncludeStockDetails: true  // Include stock details for view mode
      };
      const fullPurchaseOrderData = await getPurchaseOrders(viewFilter);
      const fullPurchaseOrder = fullPurchaseOrderData && fullPurchaseOrderData.length > 0 
        ? fullPurchaseOrderData[0] 
        : purchaseOrder; // Fallback to original if fetch fails
      
      console.log('=== HANDLEVIEW DEBUG ===');
      console.log('Raw purchaseOrder.details:', fullPurchaseOrder.details);
      console.log('First detail raw data:', fullPurchaseOrder.details?.[0]);
      console.log('First detail keys:', Object.keys(fullPurchaseOrder.details?.[0] || {}));
      console.log('First detail purchaseOrderDet_Product_Id:', fullPurchaseOrder.details?.[0]?.purchaseOrderDet_Product_Id);
      console.log('First detail purchaseOrderDet_Quantity:', fullPurchaseOrder.details?.[0]?.purchaseOrderDet_Quantity);
      console.log('First detail product_NameEn:', fullPurchaseOrder.details?.[0]?.product_NameEn);
      console.log('First detail unit_NameEn:', fullPurchaseOrder.details?.[0]?.unit_NameEn);
      
      // Set selected supplier option for display
      if (fullPurchaseOrder.purchaseOrder_Supplier_Id && fullPurchaseOrder.supplier_NameEn) {
        // Look up supplier from loaded suppliers list to get the code
        const supplier = suppliers.find(s => s.supplier_Id === fullPurchaseOrder.purchaseOrder_Supplier_Id);
        const supplierCode = supplier?.supplier_Code || fullPurchaseOrder.supplier_Code || fullPurchaseOrder.supplier_ContactNo || '';
        setSelectedSupplierOption({
          value: fullPurchaseOrder.purchaseOrder_Supplier_Id,
          label: `${fullPurchaseOrder.supplier_NameEn} — ${supplierCode}`
        });
      } else {
        setSelectedSupplierOption(null);
      }
      
      setForm({
        PurchaseOrder_Id: fullPurchaseOrder.purchaseOrder_Id,
        PurchaseOrder_InvoiceNo: fullPurchaseOrder.purchaseOrder_InvoiceNo,
        PurchaseOrder_RefNo: fullPurchaseOrder.purchaseOrder_RefNo,
        PurchaseOrder_Supplier_Id: fullPurchaseOrder.purchaseOrder_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: fullPurchaseOrder.purchaseOrder_InvoiceType_Id,
        PurchaseOrder_Date: fullPurchaseOrder.purchaseOrder_Date ? new Date(fullPurchaseOrder.purchaseOrder_Date) : new Date(),
        PurchaseOrder_DueDate: fullPurchaseOrder.purchaseOrder_DueDate ? new Date(fullPurchaseOrder.purchaseOrder_DueDate) : new Date(),
        PurchaseOrder_Amount: fullPurchaseOrder.purchaseOrder_Amount,
        PurchaseOrder_DiscountPercent: fullPurchaseOrder.purchaseOrder_DiscountPercent,
        PurchaseOrder_DiscountAmount: fullPurchaseOrder.purchaseOrder_DiscountAmount,
        PurchaseOrder_OtherAmount: fullPurchaseOrder.purchaseOrder_OtherAmount,
        PurchaseOrder_TaxAmount: fullPurchaseOrder.purchaseOrder_TaxAmount,
        PurchaseOrder_OtherCharges: fullPurchaseOrder.purchaseOrder_OtherCharges,
        PurchaseOrder_AdditionalDiscount: fullPurchaseOrder.purchaseOrder_AdditionalDiscount,
        PurchaseOrder_NetAmount: fullPurchaseOrder.purchaseOrder_NetAmount,
        PurchaseOrder_Notes: fullPurchaseOrder.purchaseOrder_Notes,
        PurchaseOrder_TotalQty: fullPurchaseOrder.purchaseOrder_TotalQty,
        PurchaseOrder_Branch_Id: fullPurchaseOrder.purchaseOrder_Branch_Id,
        PurchaseOrder_TotalAmount: fullPurchaseOrder.purchaseOrder_TotalAmount,
        PurchaseOrder_CreatedDate: fullPurchaseOrder.purchaseOrder_CreatedDate,
        PurchaseOrder_CreatedBy: fullPurchaseOrder.purchaseOrder_CreatedBy,
        PurchaseOrder_Business_Id: fullPurchaseOrder.purchaseOrder_Business_Id,
        Supplier_NameEn: fullPurchaseOrder.supplier_NameEn,
        InvoiceType_Name: fullPurchaseOrder.invoiceType_Name
      });
      
      if (fullPurchaseOrder.details && fullPurchaseOrder.details.length > 0) {
        // Ensure TotalAmount is calculated for existing details in view mode
        const detailsWithCalculatedAmounts = fullPurchaseOrder.details.map(detail => {
          if (!detail.PurchaseOrderDet_TotalAmount || detail.PurchaseOrderDet_TotalAmount === 0) {
            // Recalculate TotalAmount if missing
            const quantity = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
            const unitPrice = parseFloat(detail.PurchaseOrderDet_UnitPrice) || 0;
            const discountPercent = parseFloat(detail.PurchaseOrderDet_DiscountPercent) || 0;
            const baseAmount = quantity * unitPrice;
            const discountAmount = (baseAmount * discountPercent) / 100;
            const amountAfterDiscount = baseAmount - discountAmount;
            
            return {
              ...detail,
              PurchaseOrderDet_TotalAmount: amountAfterDiscount
            };
          }
          return detail;
        });
        setDetails(detailsWithCalculatedAmounts);
      } else {
        setDetails([{ ...defaultDetail }]);
      }
      
      setIsEditing(false);
      setIsViewMode(true);
      setShowEditForm(true); // Show the form for view mode
      setActiveTab("2");
      scrollToFormTop();
    } catch (error) {
      console.error("Error loading purchase order for view:", error);
      Swal.fire("Error", "Failed to load purchase order details", "error");
    } finally {
      setLoading(false);
    }
  };

  // View a purchase return (read-only) using new API
  const handleViewReturn = async (returnOrder) => {
    try {
      setLoading(true);
      const data = await getPurchaseReturnById(returnOrder.purchaseOrder_Id || returnOrder.PurchaseOrder_Id);
      if (!data || !data.header) {
        Swal.fire("Error", "Failed to load purchase return", "error");
        setLoading(false);
        return;
      }

      const header = data.header;
      const detailsResp = data.details || [];
      const returnedStock = data.returnedStock || [];

      setForm({
        PurchaseOrder_Id: header.purchaseOrder_Id,
        PurchaseOrder_InvoiceNo: header.purchaseOrder_InvoiceNo,
        PurchaseOrder_RefNo: header.purchaseOrder_RefNo,
        PurchaseOrder_Supplier_Id: header.purchaseOrder_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: header.purchaseOrder_InvoiceType_Id,
        PurchaseOrder_Date: header.purchaseOrder_Date ? new Date(header.purchaseOrder_Date) : new Date(),
        PurchaseOrder_DueDate: header.purchaseOrder_DueDate ? new Date(header.purchaseOrder_DueDate) : new Date(),
        PurchaseOrder_Amount: header.purchaseOrder_Amount,
        PurchaseOrder_DiscountPercent: header.purchaseOrder_DiscountPercent,
        PurchaseOrder_DiscountAmount: header.purchaseOrder_DiscountAmount,
        PurchaseOrder_OtherAmount: header.purchaseOrder_OtherAmount,
        PurchaseOrder_TaxAmount: header.purchaseOrder_TaxAmount,
        PurchaseOrder_OtherCharges: header.purchaseOrder_OtherCharges,
        PurchaseOrder_AdditionalDiscount: header.purchaseOrder_AdditionalDiscount,
        PurchaseOrder_NetAmount: header.purchaseOrder_NetAmount,
        PurchaseOrder_Notes: header.purchaseOrder_Notes,
        PurchaseOrder_TotalQty: header.purchaseOrder_TotalQty,
        PurchaseOrder_Branch_Id: header.purchaseOrder_Branch_Id,
        PurchaseOrder_TotalAmount: header.purchaseOrder_TotalAmount,
        PurchaseOrder_CreatedDate: header.purchaseOrder_CreatedDate,
        PurchaseOrder_CreatedBy: header.purchaseOrder_CreatedBy,
        PurchaseOrder_Business_Id: header.purchaseOrder_Business_Id,
        Supplier_NameEn: header.supplier_NameEn,
        InvoiceType_Name: header.invoiceType_Name
      });

      // Map details and attach returned stock per detail
      const mapped = detailsResp.map(d => {
        // Prefer strict match by return detail id from StockTransaction
        let stocks = returnedStock.filter(s =>
          (s.Return_PurchaseOrderDet_Id || s.return_PurchaseOrderDet_Id) === d.purchaseOrderDet_Id
        );

        // Fallback: legacy payloads may only have original purchase detail id
        if (!stocks.length) {
          stocks = returnedStock.filter(s =>
            (s.PurchaseStock_PurchaseOrderDet_Id || s.purchaseStock_PurchaseOrderDet_Id) === d.purchaseOrderDet_Id
          );
        }

        // Final fallback: group by Product + Unit if no detail id linkage is present
        if (!stocks.length) {
          stocks = returnedStock.filter(s =>
            (s.PurchaseStock_Product_Id || s.purchaseStock_Product_Id) === d.purchaseOrderDet_Product_Id &&
            (s.PurchaseStock_Unit_Id || s.purchaseStock_Unit_Id) === d.purchaseOrderDet_Unit_Id
          );
        }

        // Minimal diagnostics for verification
        console.log('[ReturnView] Detail->Stock mapping', {
          detailId: d.purchaseOrderDet_Id,
          productId: d.purchaseOrderDet_Product_Id,
          unitId: d.purchaseOrderDet_Unit_Id,
          matchedStocks: stocks.length
        });

        const qty = stocks.filter(s => !(s.PurchaseStock_IsBonus || s.purchaseStock_IsBonus)).length;
        const bonusQty = stocks.filter(s => (s.PurchaseStock_IsBonus || s.purchaseStock_IsBonus)).length;

        return {
          PurchaseOrderDet_Id: d.purchaseOrderDet_Id,
          PurchaseOrderDet_Product_Id: d.purchaseOrderDet_Product_Id,
          PurchaseOrderDet_Unit_Id: d.purchaseOrderDet_Unit_Id,
          PurchaseOrderDet_Quantity: qty,
          PurchaseOrderDet_BonusQuantity: bonusQty,
          PurchaseOrderDet_UnitPrice: d.purchaseOrderDet_UnitPrice,
          PurchaseOrderDet_DiscountPercent: d.purchaseOrderDet_DiscountPercent || 0,
          PurchaseOrderDet_DiscountAmount: 0,
          PurchaseOrderDet_TotalAmount: 0,
          PurchaseOrderDet_NetAmount: 0,
          PurchaseOrderDet_TaxPercent: d.purchaseOrderDet_TaxPercent || 0,
          PurchaseOrderDet_TaxAmount: 0,
          Product_NameEn: d.product_NameEn,
          Unit_NameEn: d.unit_NameEn,
          TaxDetails: d.taxDetails || [],
          stockDetails: stocks
        };
      });

      // Calculate amounts and totals
      const calc = [...mapped];
      for (let i = 0; i < calc.length; i++) {
        calculateDetailAmounts(calc, i);
      }
      setDetails(calc);
      console.log('[ReturnView] Footer totals input snapshot', calc);
      recalculateTotalsFromDetails(calc);

      setIsReturnMode(true);
      setIsEditing(false);
      setIsViewMode(true);
      setActiveTab("2");
      scrollToFormTop();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load purchase return", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    // Cleanup stock locks before clear (only for return mode)
    if (isReturnMode && temporaryFormUniqueId) {
      deleteStockSelectionLock(temporaryFormUniqueId).catch(err => 
        console.error('Error cleaning up locks on clear:', err)
      );
    }
    
    // Get user's company and branch IDs from localStorage
    const userCompanyId = localStorage.getItem("userCompanyId");
    const userBranchId = localStorage.getItem("userBranchId");
    
    // Reset form to default, but preserve company and branch if they exist in login
    const resetFormData = {
      ...defaultForm,
      PurchaseOrder_Date: new Date(),
      PurchaseOrder_DueDate: new Date()
    };
    
    // Auto-populate company if user has companyId and businesses are loaded
    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists) {
        resetFormData.PurchaseOrder_Business_Id = parseInt(userCompanyId);
        
        // Auto-populate branch if user has branchId and branches are loaded
        if (userBranchId && branches.length > 0) {
          const branchExists = branches.find(b => b.branch_Id === parseInt(userBranchId) && b.branch_Buisness_Id === parseInt(userCompanyId));
          if (branchExists) {
            resetFormData.PurchaseOrder_Branch_Id = parseInt(userBranchId);
          }
        }
      }
    }
    
    setForm(resetFormData);
    setDetails([{ ...defaultDetail }]);
    setIsEditing(false);
    setIsViewMode(false);
    setShowEditForm(false);
    setIsReturnMode(false);
    setReturnPurchaseOrderId(null);
    setOriginalInvoiceNo(null);
    setOriginalPurchaseOrderForReturn(null);
    setSelectedStockForReturn({});
    setPreloadedStockItems(null);
    setTemporaryFormUniqueId(null);
    setSelectedSupplierOption(null);
    setSupplierSearchOptions([]);
    setFormKey(prev => prev + 1);
    setIsReturnMode(false);
    setReturnPurchaseOrderId(null);
    setOriginalInvoiceNo(null);
    setOriginalPurchaseOrderForReturn(null);
    setSelectedStockForReturn({});
    setPreloadedStockItems(null);
    setTemporaryFormUniqueId(null);
    // Reset the ref so invoice number can be generated again for the auto-populated business
    lastGeneratedBusinessIdRef.current = null;
    setFormKey(prev => prev + 1);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(true);
  };

  // Options for dropdowns
  const businessOptions = useMemo(() => {
    return businesses.map(business => ({
      value: business.business_Id,
      label: business.business_NameEn
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    return branches
      .filter(branch => branch.branch_Buisness_Id === form.PurchaseOrder_Business_Id)
      .map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
  }, [branches, form.PurchaseOrder_Business_Id]);

  const supplierOptions = useMemo(() => {
    return suppliers.map(supplier => ({
      value: supplier.supplier_Id,
      label: supplier.supplier_NameEn
    }));
  }, [suppliers]);

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.product_Id,
      label: product.product_NameEn
    }));
  }, [products]);

  const unitOptions = useMemo(() => {
    return units.map(unit => ({
      value: unit.value,
      label: unit.label
    }));
  }, [units]);

  const invoiceTypeOptions = [
    { value: 1, label: "Cash" },
    { value: 2, label: "Credit" }
  ];

  // Derived lists for purchases vs returns (use API's isReturn flag strictly)
  const purchaseData = useMemo(() => (purchaseOrders || []).filter(po => po && po.isReturn !== true), [purchaseOrders]);

  const returnData = useMemo(() => (purchaseOrders || []).filter(po => po && po.isReturn === true), [purchaseOrders]);

  // Pagination
  const totalPages = Math.ceil((purchaseData.length || 0) / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = purchaseData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Export handlers
  const handleExportPurchaseSummary = async () => {
    try {
      setExportingSummary(true);
      await exportPurchaseSummaryToExcel(filter);
      Swal.fire("Success", "Purchase summary exported successfully", "success");
    } catch (error) {
      console.error("Error exporting purchase summary:", error);
      Swal.fire("Error", "Failed to export purchase summary", "error");
    } finally {
      setExportingSummary(false);
    }
  };

  const handleExportPurchaseDetails = async (purchaseOrder) => {
    try {
      const purchaseId = purchaseOrder.purchaseOrder_Id || purchaseOrder.PurchaseOrder_Id;
      setExportingPurchaseId(purchaseId);
      
      // Create filter for this specific purchase order
      const exportFilter = {
        PurchaseOrder_Id: purchaseId,
        From_Date: null,
        To_Date: null,
        PurchaseOrder_Supplier_Id: null,
        PurchaseOrder_Business_Id: null
      };
      
      await exportPurchaseDetailsToExcel(exportFilter);
      Swal.fire("Success", "Purchase details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting purchase details:", error);
      Swal.fire("Error", "Failed to export purchase details", "error");
    } finally {
      setExportingPurchaseId(null);
    }
  };

  const handleExportPurchaseReturnSummary = async () => {
    try {
      setExportingReturnSummary(true);
      await exportPurchaseReturnSummaryToExcel(filter);
      Swal.fire("Success", "Purchase return summary exported successfully", "success");
    } catch (error) {
      console.error("Error exporting purchase return summary:", error);
      Swal.fire("Error", "Failed to export purchase return summary", "error");
    } finally {
      setExportingReturnSummary(false);
    }
  };

  const handleExportPurchaseReturnDetails = async (purchaseReturn) => {
    try {
      const returnId = purchaseReturn.purchaseOrder_Id || purchaseReturn.PurchaseOrder_Id;
      setExportingReturnId(returnId);
      
      // Create filter for this specific purchase return
      const exportFilter = {
        PurchaseOrder_Id: returnId,
        From_Date: null,
        To_Date: null,
        PurchaseOrder_Supplier_Id: null,
        PurchaseOrder_Business_Id: null
      };
      
      await exportPurchaseReturnDetailsToExcel(exportFilter);
      Swal.fire("Success", "Purchase return details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting purchase return details:", error);
      Swal.fire("Error", "Failed to export purchase return details", "error");
    } finally {
      setExportingReturnId(null);
    }
  };

  // Diagnostics removed; API now supplies correct isReturn values

  if (!hasPermission) {
    return null;
  }

  const breadcrumbItem = activeTab === "2"
    ? (showEditForm && isEditing ? "Edit Purchase" : "Purchase List")
    : "Purchase Returns";

  return (
    <div className="page-content">
      <style>{`
        /* Hide spinner arrows for number inputs */
        input.no-spinner::-webkit-outer-spin-button,
        input.no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spinner[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <Container fluid>
        <Breadcrumb title="Purchase Management" breadcrumbItem={breadcrumbItem} />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Purchase List</span>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "3" ? "bold" : "normal",
                        color: activeTab === "3" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "3" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "3" ? "active" : ""}
                      onClick={() => setActiveTab("3")}
                    >
                      <i className="bx bx-reply"></i>
                      <span>Purchase Returns</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="2">
                    <div>
                      {/* Edit Form - Hidden by default, shown when edit is clicked */}
                      {/* Return Form - Shown when return mode is active */}
                      {/* View Form - Shown when view mode is active */}
                      {((showEditForm && isEditing) || isReturnMode || (showEditForm && isViewMode && !isEditing)) && (
                        <Card className="mb-4" ref={formTopRef}>
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5 className="card-title mb-0">
                                {isReturnMode ? "Purchase Return" : (isViewMode ? "View Purchase" : "Edit Purchase")}
                              </h5>
                              {!isViewMode && (
                                <Button
                                  color="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setShowEditForm(false);
                                    setIsEditing(false);
                                    setIsViewMode(false);
                                    setIsReturnMode(false);
                                    setReturnPurchaseOrderId(null);
                                    setOriginalInvoiceNo(null);
                                    setOriginalPurchaseOrderForReturn(null);
                                    setSelectedStockForReturn({});
                                    setPreloadedStockItems(null);
                                    handleClear();
                                  }}
                                >
                                  <X size={16} className="me-1" />
                                  {isReturnMode ? "Close Return Form" : "Close Edit Form"}
                                </Button>
                              )}
                            </div>
                            <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        {/* Basic Information */}
                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Company <span className="text-danger">*</span></Label>
                              <Select
                                key={`business-${formKey}`}
                                value={businessOptions.find(option => option.value === form.PurchaseOrder_Business_Id) || null}
                                onChange={(option) => handleBusinessChange(option?.value || null)}
                                options={businessOptions}
                                placeholder="Select Company"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode || isReturnMode || !!localStorage.getItem("userCompanyId")}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Branch</Label>
                              <Select
                                key={`branch-${formKey}`}
                                value={branchOptions.find(option => option.value === form.PurchaseOrder_Branch_Id) || null}
                                onChange={(option) => handleFormChange("PurchaseOrder_Branch_Id", option?.value || null)}
                                options={branchOptions}
                                placeholder="Select Branch"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={!form.PurchaseOrder_Business_Id || isViewMode || isReturnMode || !!localStorage.getItem("userBranchId")}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Invoice Number</Label>
                              <Input
                                type="text"
                                value={form.PurchaseOrder_InvoiceNo}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.PurchaseOrder_Date}
                                onChange={(date) => handleFormChange("PurchaseOrder_Date", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                disabled={isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Due Date</Label>
                              <DatePicker
                                selected={form.PurchaseOrder_DueDate}
                                onChange={(date) => handleFormChange("PurchaseOrder_DueDate", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                disabled={isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Reference Number</Label>
                              <Input
                                type="text"
                                value={form.PurchaseOrder_RefNo}
                                onChange={(e) => handleFormChange("PurchaseOrder_RefNo", e.target.value)}
                                placeholder="Enter reference number"
                                disabled={isViewMode || isReturnMode}
                                readOnly={isReturnMode}
                                style={isReturnMode ? { backgroundColor: '#f8f9fa' } : {}}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Invoice Type <span className="text-danger">*</span></Label>
                              <Select
                                key={`invoiceType-${formKey}`}
                                value={invoiceTypeOptions.find(option => option.value === form.PurchaseOrder_InvoiceType_Id) || null}
                                onChange={(option) => handleFormChange("PurchaseOrder_InvoiceType_Id", option?.value || 1)}
                                options={invoiceTypeOptions}
                                styles={selectStyles}
                                isDisabled={isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Supplier <span className="text-danger">*</span></Label>
                              <div className="d-flex gap-2 align-items-end">
                                <div style={{ flex: 1 }}>
                                  <Select
                                    key={`supplier-${formKey}`}
                                    value={selectedSupplierOption}
                                    onChange={async (option) => {
                                      setSelectedSupplierOption(option);
                                      const supplierId = option?.value || null;
                                      handleFormChange("PurchaseOrder_Supplier_Id", supplierId);
                                      
                                      // Load Purchase Orders for this supplier
                                      if (supplierId && !isViewMode && !isReturnMode) {
                                        await loadPurchaseOrdersForSupplier(supplierId);
                                      } else {
                                        // Clear Purchase Order selection if supplier is cleared
                                        setSelectedPurchaseOrderId(null);
                                        setSelectedPurchaseOrderData(null);
                                        setOriginalPurchaseOrderQuantities({});
                                        setMarkAsReceived(false);
                                      }
                                    }}
                                    options={supplierSearchOptions}
                                    placeholder="Search by name..."
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                    maxMenuHeight={200}
                                    isDisabled={isViewMode || isReturnMode}
                                    isLoading={supplierSearchLoading}
                                    noOptionsMessage={() => "Type at least 3 characters"}
                                    loadingMessage={() => "Searching..."}
                                    filterOption={null}
                                    onInputChange={(inputValue) => {
                                      console.log('🔤 Input changed:', inputValue);
                                      
                                      // Clear previous timeout
                                      if (supplierSearchTimeout) {
                                        clearTimeout(supplierSearchTimeout);
                                      }
                                      
                                      // Trim the input value before searching
                                      const trimmedValue = inputValue ? inputValue.trim() : '';
                                      
                                      // Set new timeout
                                      const timeout = setTimeout(() => {
                                        if (trimmedValue.length >= 3) {
                                          searchSuppliersManually(trimmedValue);
                                        } else {
                                          setSupplierSearchOptions([]);
                                        }
                                      }, 300);
                                      setSupplierSearchTimeout(timeout);
                                  
                                      return inputValue;
                                    }}
                                  />
                                </div>
                                {form.PurchaseOrder_Supplier_Id && !isViewMode && !isReturnMode && (
                                  <Button
                                    type="button"
                                    color="info"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        setLoadingPurchaseOrders(true);
                                        const filter = {
                                          PurchaseRequest_Supplier_Id: form.PurchaseOrder_Supplier_Id,
                                          PurchaseRequest_Is_Received: false
                                        };
                                        const data = await getPurchaseRequests(filter);
                                        
                                        if (data && data.length > 0) {
                                          setPurchaseOrdersList(data || []);
                                          setShowPurchaseOrderModal(true);
                                        } else {
                                          Swal.fire({
                                            icon: 'info',
                                            title: 'No Purchase Orders',
                                            text: 'There are no unreceived Purchase Orders for this supplier.',
                                            confirmButtonText: 'OK'
                                          });
                                        }
                                      } catch (error) {
                                        console.error("Error loading purchase orders:", error);
                                        Swal.fire({
                                          icon: 'error',
                                          title: 'Error',
                                          text: 'Failed to load Purchase Orders.',
                                          confirmButtonText: 'OK'
                                        });
                                      } finally {
                                        setLoadingPurchaseOrders(false);
                                      }
                                    }}
                                    title="Select Purchase Order"
                                    style={{ minWidth: '40px', height: '38px' }}
                                  >
                                    <i className="bx bx-list-ul"></i>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Details Section */}
                        <Row>
                          <Col md={12}>
                            <Label className="fw-bold mb-3">Purchase Details</Label>
                            <div className="table-responsive">
                              <Table bordered style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Sr.No</th>
                                    <th width="20%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Product</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Qty</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Bonus Qty</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit Price</th>
                                    <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Discount %</th>
                                    <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Tax %</th>
                                    <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Total Amount</th>
                                    <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Net Amount</th>
                                    <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details
                                    .filter(detail => detail && getDetailField(detail, 'PurchaseOrderDet_Quantity') !== undefined)
                                    .filter(detail => !isReturnMode || (Array.isArray(detail.stockDetails) && detail.stockDetails.length > 0))
                                    .map((detail, index) => {
                                    return (
                                    <tr key={index}>
                                      <td style={{ padding: '0', textAlign: 'center', border: '1px solid #dee2e6' }}>{index + 1}</td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Select
                                          key={`product-${formKey}-${index}`}
                                          value={productOptions.find(option => option.value === getDetailField(detail, 'PurchaseOrderDet_Product_Id')) || null}
                                          onChange={(option) => handleDetailChange(index, 'PurchaseOrderDet_Product_Id', option?.value || null)}
                                          options={productOptions}
                                          placeholder="Select Product"
                                          styles={{
                                            ...selectStyles,
                                            control: (provided) => ({
                                              ...provided,
                                              border: 'none',
                                              borderRadius: '0',
                                              minHeight: '38px',
                                              boxShadow: 'none'
                                            })
                                          }}
                                          isClearable
                                          isSearchable
                                          maxMenuHeight={200}
                                          isDisabled={isViewMode || isReturnMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="text"
                                          value={getDetailField(detail, 'Unit_NameEn')}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                          placeholder="Select Product"
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          max="300000"
                                          step="1"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_Quantity'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            // Only allow integers (whole numbers)
                                            if (value === '' || /^\d+$/.test(value)) {
                                              const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
                                              // Validate maximum limit
                                              if (numValue > 300000) {
                                                Swal.fire({
                                                  icon: 'error',
                                                  title: 'Quantity Limit Exceeded',
                                                  text: 'Quantity cannot exceed 300,000. Please enter a value within the limit.',
                                                  confirmButtonText: 'OK'
                                                });
                                                return;
                                              }
                                              handleDetailChange(index, 'PurchaseOrderDet_Quantity', numValue);
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            // Prevent decimal point and other non-numeric characters except backspace, delete, arrow keys
                                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                              e.preventDefault();
                                            }
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          max="100000"
                                          step="1"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_BonusQuantity'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            // Only allow integers (whole numbers)
                                            if (value === '' || /^\d+$/.test(value)) {
                                              const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
                                              // Validate maximum limit
                                              if (numValue > 100000) {
                                                Swal.fire({
                                                  icon: 'error',
                                                  title: 'Bonus Quantity Limit Exceeded',
                                                  text: 'Bonus quantity cannot exceed 100,000. Please enter a value within the limit.',
                                                  confirmButtonText: 'OK'
                                                });
                                                return;
                                              }
                                              handleDetailChange(index, 'PurchaseOrderDet_BonusQuantity', numValue);
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            // Prevent decimal point and other non-numeric characters except backspace, delete, arrow keys
                                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                              e.preventDefault();
                                            }
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_UnitPrice'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            handleDetailChange(index, 'PurchaseOrderDet_UnitPrice', value === '' ? 0 : parseFloat(value) || 0);
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          max="100"
                                          step="0.01"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_DiscountPercent'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            handleDetailChange(index, 'PurchaseOrderDet_DiscountPercent', value === '' ? 0 : parseFloat(value) || 0);
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={getDetailField(detail, 'PurchaseOrderDet_TaxPercent')}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={getDetailField(detail, 'PurchaseOrderDet_TotalAmount') || getDetailField(detail, 'purchaseOrderDet_TotalAmount') || 0}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={getDetailField(detail, 'PurchaseOrderDet_NetAmount')}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', width: '5%' }}>
                                        <div className="d-flex justify-content-center align-items-center gap-1" style={{ minHeight: '38px' }}>
                                  {isReturnMode && !isViewMode && (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => openStockModal(index)}
                                      disabled={!getDetailField(detail, 'PurchaseOrderDet_Product_Id')}
                                      style={{ 
                                        padding: '6px 10px',
                                        minWidth: '36px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px'
                                      }}
                                      title="Select Stock for Return"
                                    >
                                      <RotateCcw size={14} />
                                    </Button>
                                  )}
                                  {isReturnMode && isViewMode && (
                                    <Button
                                      type="button"
                                      color="info"
                                      size="sm"
                                      onClick={() => openStockDetailsModal(index)}
                                      disabled={!getDetailField(detail, 'PurchaseOrderDet_Product_Id')}
                                      style={{ 
                                        padding: '6px 10px',
                                        minWidth: '36px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px'
                                      }}
                                      title="View Stock Details"
                                    >
                                      <i className="bx bx-box" style={{ fontSize: '16px' }}></i>
                                    </Button>
                                  )}
                                  {!isReturnMode && (
                                    <Button
                                      type="button"
                                      color="info"
                                      size="sm"
                                      onClick={() => openStockModal(index)}
                                      disabled={!getDetailField(detail, 'PurchaseOrderDet_Product_Id')}
                                      style={{ 
                                        padding: '6px 10px',
                                        minWidth: '36px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px'
                                      }}
                                      title="Stock Details"
                                    >
                                      <i className="bx bx-box" style={{ fontSize: '16px' }}></i>
                                    </Button>
                                  )}
                                          {!isViewMode && details.length > 1 && (
                                            <Button
                                              type="button"
                                              color="danger"
                                              size="sm"
                                              onClick={() => removeDetail(index)}
                                              style={{ 
                                                padding: '6px 10px',
                                                minWidth: '36px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                              }}
                                              title="Remove Row"
                                            >
                                              <Minus size={14} />
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                            {!isViewMode && !isReturnMode && (
                              <Button
                                type="button"
                                color="success"
                                size="sm"
                                onClick={addDetail}
                                className="mt-2"
                              >
                                <Plus size={16} /> Add Row
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {/* Summary Section */}
                        <Row className="mt-4">
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>Notes</Label>
                              <Input
                                type="textarea"
                                rows="3"
                                value={form.PurchaseOrder_Notes}
                                onChange={(e) => handleFormChange("PurchaseOrder_Notes", e.target.value)}
                                placeholder="Enter any notes"
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                          {selectedPurchaseOrderId && !isViewMode && !isReturnMode && (
                            <Col md={6}>
                              <div className="mb-3">
                                <div className="form-check mt-4">
                                  <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="markAsReceived"
                                    checked={markAsReceived}
                                    onChange={(e) => setMarkAsReceived(e.target.checked)}
                                  />
                                  <Label className="form-check-label" for="markAsReceived">
                                    Mark Purchase Order as Received
                                  </Label>
                                </div>
                              </div>
                            </Col>
                          )}
                          <Col md={12}>
                            <Row>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_TotalAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Discount Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_DiscountAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Tax Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_TaxAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Other Charges</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formatNumericValue(form.PurchaseOrder_OtherCharges)}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      handleFormChange("PurchaseOrder_OtherCharges", value === '' ? 0 : parseFloat(value) || 0);
                                    }}
                                    disabled={isViewMode || isReturnMode}
                                    readOnly={isReturnMode}
                                    style={isReturnMode ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Additional Discount</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formatNumericValue(form.PurchaseOrder_AdditionalDiscount)}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      handleFormChange("PurchaseOrder_AdditionalDiscount", value === '' ? 0 : parseFloat(value) || 0);
                                    }}
                                    disabled={isViewMode || isReturnMode}
                                    readOnly={isReturnMode}
                                    style={isReturnMode ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label className="fw-bold">Net Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_NetAmount}
                                    readOnly
                                    style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>

                        {!isViewMode && (
                          <Row className="mt-4">
                            <Col>
                              <div className="d-flex gap-2">
                                <Button
                                  type="submit"
                                  color="primary"
                                  disabled={saveLoading}
                                  className="waves-effect waves-light"
                                >
                                  {saveLoading && (
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                  )}
                                  {isReturnMode ? "Create Purchase Return" : (isEditing ? "Update Purchase" : "Save Purchase")}
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleClear}
                                  className="waves-effect waves-light"
                                >
                                  Clear
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        )}

                        {isViewMode && (
                          <Row className="mt-4">
                            <Col>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={() => {
                                  setShowEditForm(false);
                                  setIsViewMode(false);
                                  setIsEditing(false);
                                  handleClear();
                                }}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-arrow-back me-1"></i>
                                Back
                              </Button>
                            </Col>
                          </Row>
                        )}
                            </Form>
                          </CardBody>
                        </Card>
                      )}


                      {/* Purchase List */}
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Purchase List
                            </h5>
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                color="primary"
                                onClick={() => setShowFilters(!showFilters)}
                                className="waves-effect waves-light"
                              >
                                {showFilters ? "Hide Filters" : "Show Filters"}
                              </Button>
                              <Button
                                color="success"
                                onClick={handleExportPurchaseSummary}
                                disabled={exportingSummary || purchaseData.length === 0}
                                className="waves-effect waves-light"
                              >
                                {exportingSummary ? (
                                  <ClipLoader size={14} color="#fff" className="me-1" />
                                ) : (
                                  <Download size={14} className="me-1" />
                                )}
                                Export Summary
                              </Button>
                              {isViewMode ? (
                                <Button
                                  color="secondary"
                                  onClick={() => {
                                    setShowEditForm(false);
                                    setIsViewMode(false);
                                    setIsEditing(false);
                                    handleClear();
                                  }}
                                  className="waves-effect waves-light"
                                >
                                  <i className="bx bx-arrow-back me-1"></i>
                                  Back
                                </Button>
                              ) : (
                                hasEditOrHigher && (
                                  <Button
                                    color="primary"
                                    onClick={() => navigate('/GmsAddPurchase')}
                                    className="waves-effect waves-light"
                                  >
                                    <i className="bx bx-plus me-1"></i>
                                    Add New Purchase
                                  </Button>
                                )
                              )}
                            </div>
                          </div>
                          
                          {showFilters && (
                            <div className="mb-3">
                              <Row className="g-3">
                                <Col md={3}>
                                  <Label>Company</Label>
                                  <Select
                                    value={businessOptions.find(option => option.value === filter.PurchaseOrder_Business_Id)}
                                    onChange={(option) => setFilter({ ...filter, PurchaseOrder_Business_Id: option?.value || null })}
                                    options={businessOptions}
                                    placeholder="Select Company"
                                    styles={selectStyles}
                                    isClearable
                                  />
                                </Col>
                                <Col md={3}>
                                  <Label>Supplier</Label>
                                  <Select
                                    value={selectedSupplierFilterOption}
                                    onChange={(option) => {
                                      setSelectedSupplierFilterOption(option);
                                      setFilter({ ...filter, PurchaseOrder_Supplier_Id: option?.value || null });
                                    }}
                                    options={supplierSearchOptions}
                                    placeholder="Search by name..."
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                    isLoading={supplierSearchLoading}
                                    noOptionsMessage={() => "Type at least 3 characters"}
                                    loadingMessage={() => "Searching..."}
                                    filterOption={null}
                                    onInputChange={(inputValue) => {
                                      console.log('🔤 Filter Input changed:', inputValue);
                                      
                                      // Clear previous timeout
                                      if (supplierSearchTimeout) {
                                        clearTimeout(supplierSearchTimeout);
                                      }
                                      
                                      // Trim the input value before searching
                                      const trimmedValue = inputValue ? inputValue.trim() : '';
                                      
                                      // Set new timeout
                                      const timeout = setTimeout(() => {
                                        if (trimmedValue.length >= 3) {
                                          searchSuppliersFilterManually(trimmedValue);
                                        } else {
                                          setSupplierSearchOptions([]);
                                        }
                                      }, 300);
                                      setSupplierSearchTimeout(timeout);
                                      
                                      return inputValue;
                                    }}
                                  />
                                </Col>
                                <Col md={3}>
                                  <Label>From Date</Label>
                                  <DatePicker
                                    selected={filter.From_Date}
                                    onChange={(date) => setFilter({ ...filter, From_Date: date })}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                    isClearable
                                  />
                                </Col>
                                <Col md={3}>
                                  <Label>To Date</Label>
                                  <DatePicker
                                    selected={filter.To_Date}
                                    onChange={(date) => setFilter({ ...filter, To_Date: date })}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                    isClearable
                                  />
                                </Col>
                                <Col md={12} className="d-flex align-items-end">
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      onClick={handleApplyFilter}
                                      className="waves-effect waves-light"
                                    >
                                      Apply Filter
                                    </Button>
                                    <Button
                                      color="secondary"
                                      onClick={handleClearFilter}
                                      className="waves-effect waves-light"
                                    >
                                      Clear Filter
                                    </Button>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          )}
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading purchase...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="4%">#</th>
                                      <th width="12%">Date</th>
                                      <th width="15%">Invoice Number</th>
                                      <th width="15%">Supplier</th>
                                      <th width="10%">Net Amount</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={`purchase-order-${item.purchaseOrder_Id || index}`}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{item.purchaseOrder_Date ? formatDateDDMMYYYY(item.purchaseOrder_Date) : 'N/A'}</td>
                                          <td>{item.purchaseOrder_InvoiceNo || 'N/A'}</td>
                                          <td>{item.supplier_NameEn || 'N/A'}</td>
                                          <td>{Number(item.purchaseOrder_NetAmount ?? 0).toFixed(2)}</td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              {/* Edit button - Commented out, can be restored if needed */}
                                              {/* {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.PurchaseOrder_Id}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.PurchaseOrder_Id ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )} */}
                                              {hasDeletePermission && (
                                                <Button
                                                  color="warning"
                                                  size="sm"
                                                  onClick={() => handlePurchaseReturn(item)}
                                                  className="waves-effect waves-light"
                                                  title="Purchase Return"
                                                >
                                                  <RotateCcw size={12} />
                                                </Button>
                                              )}
                                              <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => handleExportPurchaseDetails(item)}
                                                disabled={exportingPurchaseId === (item.purchaseOrder_Id || item.PurchaseOrder_Id)}
                                                className="waves-effect waves-light"
                                                title="Export Details"
                                              >
                                                {exportingPurchaseId === (item.purchaseOrder_Id || item.PurchaseOrder_Id) ? (
                                                  <ClipLoader size={12} color="#fff" />
                                                ) : (
                                                  <Download size={12} />
                                                )}
                                              </Button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="6" className="text-center">
                                          {hasAppliedFilter ? "No purchase found matching the filter criteria." : "No purchase available. Please add some purchase first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {totalPages}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === totalPages}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>

                  {/* Purchase Returns Tab */}
                  <TabPane tabId="3">
                    <div>
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Purchase Returns</h5>
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                color="success"
                                onClick={handleExportPurchaseReturnSummary}
                                disabled={exportingReturnSummary || returnData.length === 0}
                                className="waves-effect waves-light"
                              >
                                {exportingReturnSummary ? (
                                  <ClipLoader size={14} color="#fff" className="me-1" />
                                ) : (
                                  <Download size={14} className="me-1" />
                                )}
                                Export Summary
                              </Button>
                            </div>
                          </div>
                          <div className="table-responsive">
                            <Table className="table table-bordered table-striped table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th width="4%">#</th>
                                  <th width="12%">Date</th>
                                  <th width="20%">Return Invoice</th>
                                  <th width="15%">Supplier</th>
                                  <th width="10%">Net Amount</th>
                                  <th width="10%">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {returnData.length > 0 ? (
                                  returnData.map((item, idx) => (
                                    <tr key={`purchase-return-${item.purchaseOrder_Id || idx}`}>
                                      <td>{idx + 1}</td>
                                      <td>{item.purchaseOrder_Date ? formatDateDDMMYYYY(item.purchaseOrder_Date) : 'N/A'}</td>
                                      <td>{item.purchaseOrder_InvoiceNo || 'N/A'}</td>
                                      <td>{item.supplier_NameEn || 'N/A'}</td>
                                      <td>{Number(item.purchaseOrder_NetAmount ?? 0).toFixed(2)}</td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          <Button
                                            color="info"
                                            size="sm"
                                            onClick={() => handleViewReturn(item)}
                                            className="waves-effect waves-light"
                                          >
                                            <Eye size={12} />
                                          </Button>
                                          <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => handleExportPurchaseReturnDetails(item)}
                                            disabled={exportingReturnId === (item.purchaseOrder_Id || item.PurchaseOrder_Id)}
                                            className="waves-effect waves-light"
                                            title="Export Details"
                                          >
                                            {exportingReturnId === (item.purchaseOrder_Id || item.PurchaseOrder_Id) ? (
                                              <ClipLoader size={12} color="#fff" />
                                            ) : (
                                              <Download size={12} />
                                            )}
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="6" className="text-center">No purchase returns available.</td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Stock Selection Modal for Purchase Return */}
      {isReturnMode && (
        <StockSelectionModal
          isOpen={showStockSelectionModal}
          toggle={() => setShowStockSelectionModal(false)}
          productId={currentProduct?.product_Id}
          productName={currentProduct?.product_NameEn}
          requestedQuantity={details[currentDetailIndex]?.originalQuantity || 0}
          onStockSelected={handleStockSelectedForReturn}
          selectedStockItems={selectedStockForReturn[currentDetailIndex] || []}
          branchId={form.PurchaseOrder_Branch_Id}
          businessId={form.PurchaseOrder_Business_Id}
          preloadedStockItems={preloadedStockItems}
          temporaryFormUniqueId={temporaryFormUniqueId}
          flag={3}
          createdBy={getUserSession().userId}
        />
      )}

      {/* View-only Stock Details Modal for Purchase Return */}
      {isReturnMode && isViewMode && (
        <StockDetailsModal
          isOpen={showStockDetailsModal}
          toggle={() => setShowStockDetailsModal(false)}
          productName={currentStockDetailsProduct}
          stockDetails={currentStockDetails}
        />
      )}

      {/* Stock Details Modal */}
      <Modal isOpen={showStockModal} toggle={handleStockModalClose} size="xl">
        <style>{`
          .stock-details-modal-content {
            font-size: 0.8125rem;
          }
          .stock-details-modal-content .table {
            font-size: 0.8125rem;
          }
          .stock-details-modal-content .table th,
          .stock-details-modal-content .table td {
            font-size: 0.8125rem;
            padding: 0.4rem 0.5rem;
          }
          .stock-details-modal-content .form-control {
            font-size: 0.8125rem;
            padding: 0.375rem 0.5rem;
          }
          .stock-details-modal-content .badge {
            font-size: 0.6875rem;
            padding: 0.25rem 0.5rem;
          }
          .stock-details-modal-header {
            font-size: 0.9375rem;
          }
          .stock-details-modal-header .modal-title {
            font-size: 0.9375rem;
          }
        `}</style>
        <ModalHeader toggle={handleStockModalClose} className="stock-details-modal-header">
          Stock Details - {currentProduct?.product_NameEn}
          {previousPrices?.hasPreviousPrices && previousPrices?.previousSalesPrice && (
            <span className="ms-3 text-muted">
              (Last Sales Price: {parseFloat(previousPrices.previousSalesPrice).toFixed(2)})
            </span>
          )}
        </ModalHeader>
        <ModalBody className="stock-details-modal-content">
          {(() => {
            const detail = details[currentDetailIndex];
            if (!detail) return null;
            
            // For return mode, calculate total from available stock items
            // For regular purchase, use purchase order detail quantities
            let totalQuantity;
            if (isReturnMode) {
              // For return mode, use the stored originalAvailableStockCount if available
              // This is the most reliable source as it's stored when first loaded from database
              if (detail.originalAvailableStockCount && detail.originalAvailableStockCount > 0) {
                totalQuantity = detail.originalAvailableStockCount;
              } else {
                // Fallback: check detail.stockDetails
                const returnableStockItems = detail.stockDetails || [];
                
                if (returnableStockItems.length === 0) {
                  totalQuantity = 0;
                } else {
                  // Check if detail.stockDetails contains individual items (not batches)
                  // Individual items have PurchaseStock_Id, batches have batchQuantity
                  const isIndividualItems = (returnableStockItems[0].PurchaseStock_Id !== undefined || 
                                            returnableStockItems[0].purchaseStock_Id !== undefined) &&
                                           !(returnableStockItems[0].batchQuantity !== undefined || 
                                             returnableStockItems[0].PurchaseStock_BatchQuantity !== undefined);
                  
                  if (isIndividualItems) {
                    // Individual items: count them directly - this is the source of truth from database
                    totalQuantity = returnableStockItems.length;
                  } else {
                    // Batches format: try to get from _originalAvailableCount stored in batches
                    const batchWithCount = stockDetails.find(batch => batch._originalAvailableCount);
                    const countFromBatches = batchWithCount ? batchWithCount._originalAvailableCount : null;
                    if (countFromBatches && countFromBatches > 0) {
                      totalQuantity = countFromBatches;
                    } else {
                      // Last resort: sum originalStockIds.length (but this might be corrupted)
                      // Remove duplicates first to avoid double counting
                      const allOriginalIds = new Set();
                      returnableStockItems.forEach(batch => {
                        if (batch.originalStockIds && Array.isArray(batch.originalStockIds)) {
                          batch.originalStockIds.forEach(id => allOriginalIds.add(id));
                        }
                      });
                      totalQuantity = allOriginalIds.size;
                    }
                  }
                }
              }
            } else {
              const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
              const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
              totalQuantity = quantity + bonusQuantity;
            }
            
            // Use batchQuantity for both return mode and regular purchase (user can edit it in return mode)
            const allocatedQuantity = stockDetails.reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
            const remainingQuantity = totalQuantity - allocatedQuantity;
            
            return (
              <div>
                <div className="mb-3 p-2 bg-light rounded">
                  {isReturnMode ? (
                    <>
                      <strong>Available Stock:</strong> {totalQuantity} items | 
                      <strong className="ms-2">Returning:</strong> {allocatedQuantity} | 
                      <strong className="ms-2">Remaining:</strong> <span className={remainingQuantity < 0 ? 'text-danger' : remainingQuantity > 0 ? 'text-warning' : 'text-success'}>{remainingQuantity}</span>
                    </>
                  ) : (
                    <>
                      {(() => {
                        const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
                        const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
                        
                        // In view mode, only show quantities without allocated/remaining
                        if (isViewMode) {
                          return (
                            <>
                              <strong>Regular Quantity:</strong> {quantity}
                              <br />
                              <strong>Bonus Quantity:</strong> {bonusQuantity}
                            </>
                          );
                        }
                        
                        // In edit mode, show allocated and remaining
                        const regularAllocated = stockDetails
                          .filter(batch => !(batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
                          .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
                        const bonusAllocated = stockDetails
                          .filter(batch => (batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
                          .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
                        
                        const regularRemaining = quantity - regularAllocated;
                        const bonusRemaining = bonusQuantity - bonusAllocated;
                        
                        return (
                          <>
                            <strong>Regular Quantity:</strong> {quantity} | 
                            <strong className="ms-2">Allocated:</strong> {regularAllocated} | 
                            <strong className="ms-2">Remaining:</strong> <span className={regularRemaining < 0 ? 'text-danger' : regularRemaining > 0 ? 'text-warning' : 'text-success'}>{regularRemaining}</span>
                            <br />
                            <strong>Bonus Quantity:</strong> {bonusQuantity} | 
                            <strong className="ms-2">Allocated:</strong> {bonusAllocated} | 
                            <strong className="ms-2">Remaining:</strong> <span className={bonusRemaining < 0 ? 'text-danger' : bonusRemaining > 0 ? 'text-warning' : 'text-success'}>{bonusRemaining}</span>
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
                <div className="table-responsive">
                  <Table bordered>
                    <thead className="table-light">
                      <tr>
                        <th width="5%">Sr.No</th>
                        <th width="12%">Quantity</th>
                        <th width="15%">Batch Number</th>
                        <th width="15%">Expiry Date</th>
                        <th width="15%">Sales Price</th>
                        <th width="15%">Cost Price</th>
                        <th width="10%">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockDetails.map((stock, index) => {
                        return (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={stock.batchQuantity || 0}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleStockChange(index, 'batchQuantity', value === '' ? 0 : parseFloat(value) || 0);
                              }}
                              placeholder="Qty"
                              disabled={isViewMode}
                            />
                          </td>
                          <td>
                            <Input
                              type="text"
                              value={stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || ''}
                              onChange={(e) => handleStockChange(index, 'PurchaseStock_BatchNo', e.target.value)}
                              placeholder="Enter batch number"
                              disabled={isViewMode}
                            />
                          </td>
                          <td>
                            {currentProduct?.expiryRequired ? (
                              <Input
                                type="text"
                                value={stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || ''}
                                onChange={(e) => {
                                  const dateStr = e.target.value;
                                  // Allow typing and store as string
                                  handleStockChange(index, 'PurchaseStock_ExpDate', dateStr);
                                }}
                                placeholder="DD/MM/YYYY"
                                maxLength="10"
                                disabled={isViewMode}
                              />
                            ) : (
                              <Input
                                type="text"
                                value="N/A"
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            )}
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formatNumericValue(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0)}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleStockChange(index, 'PurchaseStock_SalesPrice', value === '' ? 0 : parseFloat(value) || 0);
                              }}
                              placeholder="Sales price"
                              disabled={isViewMode}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={stock.purchaseStock_CostPrice || stock.PurchaseStock_CostPrice || (() => {
                                const detail = details[currentDetailIndex];
                                if (!detail) return 0;
                                const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
                                const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
                                const totalQuantity = quantity + bonusQuantity;
                                const unitPrice = parseFloat(detail.purchaseOrderDet_UnitPrice || detail.PurchaseOrderDet_UnitPrice) || 0;
                                const totalAmount = parseFloat(quantity * unitPrice) || 0;
                                return totalQuantity > 0 ? (totalAmount / totalQuantity).toFixed(2) : 0;
                              })()}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </td>
                          <td>
                            <span className={`badge ${(stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus) ? 'bg-warning' : 'bg-primary'}`}>
                              {(stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus) ? 'Bonus' : 'Regular'}
                            </span>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                {!isViewMode && !isReturnMode && (
                  <div className="mt-3 d-flex gap-2">
                    {(() => {
                      const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
                      const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
                      
                      const regularAllocated = stockDetails
                        .filter(batch => !(batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
                        .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
                      const bonusAllocated = stockDetails
                        .filter(batch => (batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
                        .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
                      
                      const regularRemaining = quantity - regularAllocated;
                      const bonusRemaining = bonusQuantity - bonusAllocated;
                      
                      return (
                        <>
                          {regularRemaining > 0 && (
                            <Button color="success" onClick={() => addNextBatch(false)}>
                              <Plus size={16} className="me-1" />
                              Add Regular Batch ({regularRemaining} remaining)
                            </Button>
                          )}
                          {bonusRemaining > 0 && (
                            <Button color="warning" onClick={() => addNextBatch(true)}>
                              <Plus size={16} className="me-1" />
                              Add Bonus Batch ({bonusRemaining} remaining)
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                {/* Remove Add Next Batch button for return mode - users should only select from available stock */}
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          {!isViewMode && (
            <Button color="primary" onClick={saveStockDetails}>
              Save Stock Details
            </Button>
          )}
          <Button color="secondary" onClick={handleStockModalClose}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Purchase Order Selection Modal */}
      <Modal isOpen={showPurchaseOrderModal} toggle={() => setShowPurchaseOrderModal(false)} size="lg">
        <ModalHeader toggle={() => setShowPurchaseOrderModal(false)}>
          Select Purchase Order
        </ModalHeader>
        <ModalBody>
          {loadingPurchaseOrders ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading Purchase Orders...</p>
            </div>
          ) : purchaseOrdersList.length > 0 ? (
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="table-light">
                  <tr>
                    <th>Order No</th>
                    <th>Date</th>
                    <th>Net Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrdersList.map((po) => {
                    const poId = po.purchaseRequest_Id || po.PurchaseRequest_Id;
                    const orderNo = po.purchaseRequest_OrderNo || po.PurchaseRequest_OrderNo;
                    const poDate = po.purchaseRequest_Date || po.PurchaseRequest_Date;
                    const netAmount = po.purchaseRequest_NetAmount || po.PurchaseRequest_NetAmount || 0;
                    return (
                      <tr key={poId}>
                        <td>{orderNo || 'N/A'}</td>
                        <td>{poDate ? formatDateDDMMYYYY(poDate) : 'N/A'}</td>
                        <td>{Number(netAmount).toFixed(2)}</td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handlePurchaseOrderSelect(po)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">
              <p>No unreceived Purchase Orders found for this supplier.</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPurchaseOrderModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
